/**
 * Simplified Excel-like formula language for grid columns. A formula is defined once per
 * COLUMN and evaluated row by row (the calculated-column model of a table, not the per-cell
 * model of a spreadsheet). Full contract: docs/Grid/FORMULAS.md.
 *
 * This module is pure: no React, no Supabase, no Grid. It knows nothing about columns beyond
 * a `ColumnScope` that maps visible names to stable `field` identifiers.
 *
 * Supported
 *   Operators:  `+ - * / ^ ( )`, postfix `%` (divides by 100), and `&` (text concatenation).
 *   Precedence: `%` (50) > unary `+`/`-` (40) > `^` (30) > `*` `/` (20) > binary `+` `-` (10)
 *               > `&` (5) — the same order Excel uses.
 *   Excel quirks, both deliberate: unary binds TIGHTER than `^`, so `-2^2` is 4; and `^` is
 *               LEFT-associative, so `2^3^2` is 64 (not 512 — that is the mathematical
 *               convention, and Excel does not follow it).
 *   References: `[Column]`, `[@Column]`, `[Column]12`, rectangular `:` ranges, and apostrophe-
 *               quoted sheet names. Canonical text stores stable sheet/column/row identities.
 *   Numbers:    the decimal separator comes from the locale; the stored form is always `.`.
 *   Text:       `"literal"`, with `""` for a literal quote inside it, as in Excel.
 *   Functions:  only the ones in `FUNCTIONS` (`CHAR`, `SUM`, `IF`, `IFS`, `IFERROR`, ...), arguments separated by the
 *               locale's separator (`;` with a comma decimal, `,` with a dot decimal).
 *
 * NOT supported (each rejected with a Spanish message, never a generic parse failure):
 *   any function outside `FUNCTIONS`, logical operators, and direct cross-sheet
 *   cell references. Cross-sheet whole-column/range collections are supported for aggregators.
 *
 * Empty cells follow Sheets, not Excel: a reference to an empty cell yields `empty`, and the
 * ARITHMETIC OPERATORS are what coerce it to 0 — the reference never does. That is what lets a
 * future average function ignore empties without touching reference handling. Consequence
 * worth knowing before reporting it as a bug: the only formula that returns `empty` is a bare
 * reference; `[A]+[B]` with both cells empty shows 0, not blank.
 */

import { costBasis, type CostMethod, type CostTransaction } from './costBasis'
import { dateToSerial, parseGridDate, serialToDate } from './dateSerial'
import { esFormatoFecha, formatearExcel } from './excelFormat'
import { isGlobalFormatName, resolveGlobalFormatPattern } from './globalFormats'

/**
 * Decimal separator and the argument separator that follows it, exactly as Excel pairs them
 * per locale. `argument` separates the arguments of a function call — `CHAR(10)` takes one, but
 * the pairing has to be right the day a two-argument function arrives.
 */
export interface FormulaLocale {
  decimal: '.' | ','
  argument: ',' | ';'
}

/** What gets STORED: always `.` decimal, so flipping the user's separator cannot change the
 *  meaning of an existing formula. */
export const CANONICAL_LOCALE: FormulaLocale = { decimal: '.', argument: ',' }

/** Locale pair for a given decimal separator (`,` → `;`, like Excel es-AR). */
export function localeFor(decimal: '.' | ','): FormulaLocale {
  return decimal === ',' ? { decimal: ',', argument: ';' } : { decimal: '.', argument: ',' }
}

// ── Values ────────────────────────────────────────────────────────────────────────────────────

/** Excel's error codes across English, Spanish, and Portuguese. */
export type CanonicalErrorCode = '#DIV/0!' | '#VALUE!' | '#REF!' | '#NAME?' | '#NUM!' | '#N/A'
export type SpanishErrorCode = '#¡DIV/0!' | '#¡VALOR!' | '#¡REF!' | '#¡NOMBRE?' | '#¡NUM!' | '#N/D'
export type ErrorCode =
  | CanonicalErrorCode
  | SpanishErrorCode
  | '#VALOR!'
  | '#NOME?'
  | '#NÚM!'

/** Normalizes an Excel error code to English canonical form. */
export function localizeErrorCode(code: string, _locale?: FormulaLocale | string): string {
  switch (code) {
    case '#DIV/0!':
    case '#¡DIV/0!':
      return '#DIV/0!'
    case '#VALUE!':
    case '#¡VALOR!':
    case '#VALOR!':
      return '#VALUE!'
    case '#REF!':
    case '#¡REF!':
      return '#REF!'
    case '#NAME?':
    case '#¡NOMBRE?':
    case '#NOME?':
      return '#NAME?'
    case '#NUM!':
    case '#¡NUM!':
    case '#NÚM!':
      return '#NUM!'
    case '#N/A':
    case '#N/D':
      return '#N/A'
    default:
      return code
  }
}

/**
 * The value a formula produces for one row. Deliberately NOT `number`: the empty semantics
 * above are impossible to add later without touching every operator and every test.
 *
 * `text` is distinct from `empty`: `""` is a value the user asked for, an empty cell is not.
 * Every consumer must discriminate on `kind` with explicit branches — never
 * `v.kind === 'number' ? v.n : 0` — so that adding a member surfaces every place that needs
 * review.
 */
export type FormulaValue =
  | { kind: 'empty' }
  | { kind: 'number'; n: number }
  | { kind: 'text'; s: string }
  | { kind: 'error'; code: ErrorCode }
  /** `cols` is the width of a rectangular range (row-major `values`); absent means one column. */
  | { kind: 'collection'; values: readonly FormulaValue[]; cols?: number }

export const EMPTY: FormulaValue = { kind: 'empty' }
export const number = (n: number): FormulaValue => ({ kind: 'number', n })
export const text = (s: string): FormulaValue => ({ kind: 'text', s })
export const error = (code: ErrorCode): FormulaValue => ({ kind: 'error', code })
export const collection = (values: readonly FormulaValue[], cols = 1): FormulaValue =>
  cols > 1 ? { kind: 'collection', values, cols } : { kind: 'collection', values }

// ── AST ───────────────────────────────────────────────────────────────────────────────────────

export type ComparisonOperator = '=' | '<>' | '<' | '>' | '<=' | '>='
export type BinaryOperator = '+' | '-' | '*' | '/' | '^' | '&' | ComparisonOperator

export const isComparison = (op: BinaryOperator): op is ComparisonOperator =>
  op === '=' || op === '<>' || op === '<' || op === '>' || op === '<=' || op === '>='

export type FormulaRowReference = 'column' | 'current' | { id: string }

export interface FormulaReference {
  field: string
  row: FormulaRowReference
  sheetId?: string
  /** Set only for a reference into ANOTHER document. Cross-document data is not loaded yet, so
   *  the runtime resolves these to `#¡REF!`; the grammar, serializer and editor handle them
   *  fully, which is what lets the syntax exist before the pipeline does. */
  documentId?: string
}

/** Character range in the source text, used to underline the offending part in the editor. */
interface Span {
  start: number
  end: number
}

/**
 * The AST keeps what the user actually wrote, and that is not cosmetic:
 *   - `group` is a real node, because without it `(1+2)*3` would serialise back as `1+2*3`.
 *   - `number` keeps its original `text`, the only way a round trip returns `0.1+0.2` instead
 *     of `0.30000000000000004`.
 *   - every node carries a span, which feeds the editor's error underline.
 *   - `call` keeps the name as the user typed it only in `text`; `name` is upper-cased, which is
 *     what both the evaluator and the serializer use, so `char(10)` stores as `CHAR(10)`.
 */
export type AstNode = (
  | ({ kind: 'number'; n: number; text: string } & Span)
  | ({ kind: 'text'; s: string } & Span)
  | ({ kind: 'call'; name: string; args: AstNode[] } & Span)
  | ({ kind: 'ref'; field: string; row?: FormulaRowReference; sheetId?: string; documentId?: string } & Span)
  | ({ kind: 'range'; from: Extract<AstNode, { kind: 'ref' }>; to: Extract<AstNode, { kind: 'ref' }> } & Span)
  | ({ kind: 'unary'; op: '+' | '-'; arg: AstNode } & Span)
  | ({ kind: 'binary'; op: BinaryOperator; left: AstNode; right: AstNode } & Span)
  | ({ kind: 'percent'; arg: AstNode } & Span)
  | ({ kind: 'group'; arg: AstNode } & Span)
  /** A variable bound by an enclosing `LET`, stored as written (lookups are case-insensitive). */
  | ({ kind: 'name'; name: string } & Span)
  // Text that did not parse, kept verbatim so a half-written formula survives a save/load round
  // trip instead of being thrown away. A successful parse NEVER produces one: it is minted by
  // `fromStored` when decoding, and by the editor when the user saves a draft that does not
  // parse. It evaluates to `#¡NOMBRE?` and is refused by the SQL and inverse translators.
  | ({ kind: 'raw'; text: string } & Span)
) & { nl?: boolean }

// ── Precedence ────────────────────────────────────────────────────────────────────────────────

/**
 * `^` associativity. Excel and Sheets evaluate it LEFT to right (`2^3^2` = 64); the
 * mathematical convention is right (512). This is the only expression where the two differ, so
 * it lives in one constant: the test documents the choice and reversing it is a one-line change.
 */
const POWER_ASSOC: 'left' | 'right' = 'left'

/** Binding power of each binary operator: [left, right]. `&` sits BELOW `+`/`-`, as in Excel:
 *  `1+2&"x"` is `"3x"`, not `1+(2&"x")`. */
const BP: Record<BinaryOperator, readonly [number, number]> = {
  // Comparisons sit below everything, as in Excel: `1+1=2` is `(1+1)=2`, `"a"&"b"="ab"` is true.
  '=': [1, 2],
  '<>': [1, 2],
  '<': [1, 2],
  '>': [1, 2],
  '<=': [1, 2],
  '>=': [1, 2],
  '&': [5, 6],
  '+': [10, 11],
  '-': [10, 11],
  '*': [20, 21],
  '/': [20, 21],
  '^': [30, POWER_ASSOC === 'left' ? 31 : 29],
}

/** Unary binds TIGHTER than `^` (40 > 30). That single fact is what makes `-2^2` equal 4; no
 *  special case is needed anywhere in the parser. */
const BP_UNARY = 40

/** Postfix `%` binds tighter than everything: `-2%` is `-(2%)`, `2^2%` is `2^0.02`. */
const BP_PERCENT = 50

// ── Functions ─────────────────────────────────────────────────────────────────────────────────

interface FunctionSpec {
  arity: number | { min: number; max?: number }
  syntax?: string
  description?: string
  /**
   * Receives the RAW values, without the `empty` → 0 coercion the arithmetic operators apply:
   * each function declares its own empty policy (see `arithmetic` below, which the ones that
   * want the numeric policy call themselves).
   */
  call: (args: readonly FormulaValue[]) => FormulaValue
}

export interface FunctionMetadata {
  name: string
  syntax: string
  description: string
}

function wildcardToRegExp(pattern: string): RegExp {
  let rx = ''
  let i = 0
  while (i < pattern.length) {
    const c = pattern.charAt(i)
    if (c === '~') {
      if (i + 1 < pattern.length) {
        rx += pattern.charAt(i + 1).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        i += 2
        continue
      }
    }
    if (c === '?') {
      rx += '.'
    } else if (c === '*') {
      rx += '.*'
    } else {
      rx += c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }
    i++
  }
  return new RegExp(`^${rx}$`, 'i')
}

function matchWildcard(pattern: string, target: string): boolean {
  try {
    return wildcardToRegExp(pattern).test(target)
  } catch {
    return false
  }
}

function compareFormulaValues(
  a: FormulaValue,
  b: FormulaValue,
  matchMode: number,
): { match: boolean; cmp?: number } {
  if (a.kind === 'error' || b.kind === 'error' || a.kind === 'collection' || b.kind === 'collection') {
    return { match: false }
  }

  let valA: FormulaValue = a
  let valB: FormulaValue = b

  if (valA.kind === 'empty' && valB.kind === 'number') valA = number(0)
  if (valB.kind === 'empty' && valA.kind === 'number') valB = number(0)
  if (valA.kind === 'empty' && valB.kind === 'text') valA = text('')
  if (valB.kind === 'empty' && valA.kind === 'text') valB = text('')

  if (valA.kind === 'number' && valB.kind === 'number') {
    const diff = valA.n - valB.n
    return { match: diff === 0, cmp: diff < 0 ? -1 : diff > 0 ? 1 : 0 }
  }

  if (valA.kind === 'text' && valB.kind === 'text') {
    if (matchMode === 2) {
      const isMatch = matchWildcard(valA.s, valB.s)
      const cmpStr = valA.s.localeCompare(valB.s, undefined, { sensitivity: 'base' })
      return { match: isMatch, cmp: cmpStr < 0 ? -1 : cmpStr > 0 ? 1 : 0 }
    }
    const cmpStr = valA.s.localeCompare(valB.s, undefined, { sensitivity: 'base' })
    return { match: cmpStr === 0, cmp: cmpStr < 0 ? -1 : cmpStr > 0 ? 1 : 0 }
  }

  if (valA.kind === 'empty' && valB.kind === 'empty') {
    return { match: true, cmp: 0 }
  }

  return { match: false }
}

// ── Criteria and aggregates ─────────────────────────────────────────────────────────────────

/** Flattens collections so COUNT/COUNTA/AVERAGE see the same members as SUM. */
function flattenMembers(value: FormulaValue, out: FormulaValue[] = []): FormulaValue[] {
  if (value.kind === 'collection') {
    for (const member of value.values) flattenMembers(member, out)
  } else {
    out.push(value)
  }
  return out
}

/** One level of collection, for pairwise criteria ranges: index i in every range is one row. */
const membersOf = (value: FormulaValue): readonly FormulaValue[] =>
  value.kind === 'collection' ? value.values : [value]

/**
 * The numeric value of a member, or `null` when the member is not numeric.
 * Text inside a collection is ignored (Excel's range rule); a scalar numeric text or date
 * participates, as it does in arithmetic. An error is returned as its code.
 */
function numericMember(value: FormulaValue, fromCollection: boolean): number | ErrorCode | null {
  switch (value.kind) {
    case 'error':
      return value.code
    case 'number':
      return value.n
    case 'empty':
      return null
    case 'text': {
      if (fromCollection) return null
      const n = numericText(value.s)
      if (n !== null) return n
      const d = parseGridDate(value.s)
      return d === null ? '#¡VALOR!' : dateToSerial(d)
    }
    case 'collection':
      return '#¡VALOR!'
  }
}

type NumericValues = { ok: true; values: number[] } | { ok: false; error: ErrorCode }

/** Numbers for AVERAGE/MAX/MIN, ignoring text and empties inside collections. */
function collectNumbers(args: readonly FormulaValue[]): NumericValues {
  const values: number[] = []
  for (const arg of args) {
    const members = arg.kind === 'collection' ? flattenMembers(arg) : [arg]
    for (const member of members) {
      const n = numericMember(member, arg.kind === 'collection')
      if (typeof n === 'string') return { ok: false, error: n }
      if (n !== null) values.push(n)
    }
  }
  return { ok: true, values }
}

function countValues(args: readonly FormulaValue[]): FormulaValue {
  let count = 0
  for (const arg of args) {
    if (arg.kind === 'collection') {
      for (const member of flattenMembers(arg)) {
        if (member.kind === 'number') count++
      }
      continue
    }
    switch (arg.kind) {
      case 'number':
        count++
        break
      case 'error':
        return arg
      case 'text':
        if (numericText(arg.s) !== null || parseGridDate(arg.s) !== null) count++
        break
      case 'empty':
        break
    }
  }
  return number(count)
}

function countNonEmpty(args: readonly FormulaValue[]): FormulaValue {
  let count = 0
  for (const arg of args) {
    const members = arg.kind === 'collection' ? flattenMembers(arg) : [arg]
    for (const member of members) if (member.kind !== 'empty') count++
  }
  return number(count)
}

interface ParsedCriteria {
  readonly op: ComparisonOperator
  readonly operand: FormulaValue
  readonly wildcard: boolean
  readonly blank: boolean
}

type CriteriaResult = { ok: true; criteria: ParsedCriteria } | { ok: false; error: FormulaValue }

/** Excel's criteria vocabulary: a value, or text with an optional comparison prefix and wildcards. */
function parseCriteria(value: FormulaValue): CriteriaResult {
  if (value.kind === 'collection') return { ok: false, error: error('#¡VALOR!') }
  if (value.kind === 'error') return { ok: false, error: value }
  if (value.kind === 'number') {
    return { ok: true, criteria: { op: '=', operand: value, wildcard: false, blank: false } }
  }
  if (value.kind === 'empty') {
    return { ok: true, criteria: { op: '=', operand: EMPTY, wildcard: false, blank: true } }
  }

  const match = value.s.match(/^(<=|>=|<>|=|<|>)([\s\S]*)$/)
  const op = (match?.[1] ?? '=') as ComparisonOperator
  const rest = match?.[2] ?? value.s
  const trimmed = rest.trim()
  if (trimmed === '') {
    return { ok: true, criteria: { op, operand: EMPTY, wildcard: false, blank: op === '=' } }
  }
  const n = numericText(trimmed)
  if (n !== null) {
    return { ok: true, criteria: { op, operand: number(n), wildcard: false, blank: false } }
  }
  const d = parseGridDate(trimmed)
  if (d !== null) {
    return { ok: true, criteria: { op, operand: number(dateToSerial(d)), wildcard: false, blank: false } }
  }
  return {
    ok: true,
    criteria: { op, operand: text(rest), wildcard: op === '=' || op === '<>', blank: false },
  }
}

function equalsCriteria(value: FormulaValue, criteria: ParsedCriteria): boolean {
  if (criteria.blank) {
    return value.kind === 'empty' || (value.kind === 'text' && value.s === '')
  }
  if (criteria.operand.kind === 'number') {
    if (value.kind === 'number') return value.n === criteria.operand.n
    if (value.kind === 'text') {
      const n = numericText(value.s)
      return n !== null && n === criteria.operand.n
    }
    return false
  }
  if (criteria.operand.kind === 'text') {
    if (value.kind === 'text') {
      return criteria.wildcard
        ? matchWildcard(criteria.operand.s, value.s)
        : value.s.localeCompare(criteria.operand.s, undefined, { sensitivity: 'base' }) === 0
    }
    return value.kind === 'empty' && criteria.operand.s === ''
  }
  if (criteria.operand.kind === 'empty') return value.kind === 'empty'
  return false
}

function compareCriteria(value: FormulaValue, criteria: ParsedCriteria): number | undefined {
  const operand = criteria.operand
  if (value.kind === 'error' || operand.kind === 'error') return undefined
  if (value.kind === 'number' && operand.kind === 'number') return Math.sign(value.n - operand.n)
  if (value.kind === 'text' && operand.kind === 'number') {
    const n = numericText(value.s)
    return n === null ? undefined : Math.sign(n - operand.n)
  }
  if (value.kind === 'text' && operand.kind === 'text') {
    return Math.sign(value.s.localeCompare(operand.s, undefined, { sensitivity: 'base' }))
  }
  return undefined
}

function matchesCriteria(value: FormulaValue, criteria: ParsedCriteria): boolean {
  if (value.kind === 'error') return false
  const equal = equalsCriteria(value, criteria)
  if (criteria.op === '=') return equal
  if (criteria.op === '<>') return !equal
  const cmp = compareCriteria(value, criteria)
  if (cmp === undefined) return false
  switch (criteria.op) {
    case '<':
      return cmp < 0
    case '>':
      return cmp > 0
    case '<=':
      return cmp <= 0
    case '>=':
      return cmp >= 0
    default:
      return false
  }
}

interface CriteriaPair {
  readonly values: readonly FormulaValue[]
  readonly criteria: ParsedCriteria
}

type PairsResult = { ok: true; pairs: readonly CriteriaPair[] } | { ok: false; error: FormulaValue }

/** `rangeIndexes` point at each range; its criteria is the following argument. */
function buildCriteriaPairs(args: readonly FormulaValue[], rangeIndexes: readonly number[]): PairsResult {
  const pairs: CriteriaPair[] = []
  for (const index of rangeIndexes) {
    const criteriaValue = args[index + 1]
    if (criteriaValue === undefined) return { ok: false, error: error('#¡VALOR!') }
    const parsed = parseCriteria(criteriaValue)
    if (!parsed.ok) return parsed
    pairs.push({ values: membersOf(args[index] ?? EMPTY), criteria: parsed.criteria })
  }
  return { ok: true, pairs }
}

function matchesEvery(index: number, pairs: readonly CriteriaPair[]): boolean {
  for (const pair of pairs) {
    const value = pair.values[index]
    if (value === undefined || !matchesCriteria(value, pair.criteria)) return false
  }
  return true
}

function countMatching(pairs: readonly CriteriaPair[]): FormulaValue {
  if (pairs.length === 0) return number(0)
  const size = Math.min(...pairs.map((pair) => pair.values.length))
  let count = 0
  for (let index = 0; index < size; index++) if (matchesEvery(index, pairs)) count++
  return number(count)
}

type MatchedAggregate = 'sum' | 'average' | 'max' | 'min'

function aggregateMatching(
  source: FormulaValue,
  pairs: readonly CriteriaPair[],
  aggregate: MatchedAggregate,
): FormulaValue {
  const values = membersOf(source)
  if (pairs.length === 0) return aggregate === 'average' ? error('#¡DIV/0!') : number(0)
  const size = Math.min(values.length, ...pairs.map((pair) => pair.values.length))
  let total = 0
  let count = 0
  let best: number | undefined

  for (let index = 0; index < size; index++) {
    if (!matchesEvery(index, pairs)) continue
    const member = values[index]
    if (member === undefined) continue
    if (member.kind === 'error') return member
    if (member.kind !== 'number') continue
    total += member.n
    count++
    if (best === undefined || (aggregate === 'max' ? member.n > best : member.n < best)) best = member.n
  }

  switch (aggregate) {
    case 'sum':
      return finite(total)
    case 'average':
      return count === 0 ? error('#¡DIV/0!') : finite(total / count)
    case 'max':
    case 'min':
      return number(best ?? 0)
  }
}

type Shape = { kind: 'shape'; values: readonly FormulaValue[]; rows: number; cols: number }

/** A value as a rectangle: a scalar is 1×1, a collection is row-major with `cols` columns. */
function shapeOf(value: FormulaValue): Shape | Extract<FormulaValue, { kind: 'error' }> {
  if (value.kind === 'error') return value
  if (value.kind !== 'collection') return { kind: 'shape', values: [value], rows: 1, cols: 1 }
  const cols = value.cols ?? 1
  return { kind: 'shape', values: value.values, rows: Math.ceil(value.values.length / cols), cols }
}

/** The range indexes for COUNTIFS / AVERAGEIFS / MAXIFS / MINIFS: every other argument. */
const alternatingRangeIndexes = (start: number, length: number): number[] => {
  const indexes: number[] = []
  for (let index = start; index < length; index += 2) indexes.push(index)
  return indexes
}

/**
 * The whole function vocabulary, keyed by UPPER-CASE name. A name outside this table is a parse
 * error naming the function, not a generic failure — the same treatment an unknown column gets.
 */
/** A pair's quote for FXRATE: the formula engine does not load the history itself. */
export type FxRateProvider = (
  pair: { from: string; to: string; market: string; side: string },
  day: string,
) => number | null | undefined
let fxRateProvider: FxRateProvider | null = null
/** Set by the exchange-rate history module once it is imported in the app. */
export function setFxRateProvider(provider: FxRateProvider | null): void {
  fxRateProvider = provider
}
const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const FUNCTIONS: Record<string, FunctionSpec> = {  /** Excel's CHAR: code → character. `CHAR(10)` is the line break used inside a cell. Excel's
   *  own domain is 1..255 and it truncates a fractional code; outside that it is `#¡VALOR!`. */
  CHAR: {
    arity: 1,
    syntax: 'CHAR(código)',
    description: 'Devuelve el carácter especificado por un código numérico (ej. 10 para salto de línea).',
    // `args` arrives as a plain array, so it is re-formed into a 1-tuple: the
    // arity is already guaranteed by the parser, and this is what lets
    // `arithmetic` hand back a `number` rather than `number | undefined`.
    call: (args) =>
      arithmetic([args[0] ?? EMPTY], ([code]) => {
        const n = Math.trunc(code)
        return n >= 1 && n <= 255 ? text(String.fromCharCode(n)) : error('#¡VALOR!')
      }),
  },
  /** Excel's ABS: the number without its sign. Text coerces as in any arithmetic. */
  ABS: {
    arity: 1,
    syntax: 'ABS(número)',
    description: 'Devuelve el valor absoluto de un número.',
    call: (args) => arithmetic([args[0] ?? EMPTY], ([n]) => number(Math.abs(n))),
  },
  /** SUM accepts scalars and collections. Text and empty values inside collections are ignored,
   * while scalar text keeps Excel's normal arithmetic coercion. The first error propagates. */
  SUM: {
    arity: { min: 1 },
    syntax: 'SUM(valor1; [valor2]; ...)',
    description: 'Suma todos los números de un rango o lista de celdas.',
    call: (args) => {
      let total = 0
      const add = (value: FormulaValue, fromCollection: boolean): FormulaValue | null => {
        if (value.kind === 'error') return value
        if (value.kind === 'collection') {
          for (const member of value.values) {
            const failed = add(member, true)
            if (failed !== null) return failed
          }
          return null
        }
        if (value.kind === 'empty' || (fromCollection && value.kind === 'text')) return null
        const numeric = arithmetic([value], ([n]) => number(n))
        if (numeric.kind === 'error') return numeric
        if (numeric.kind !== 'number') return error('#¡VALOR!')
        total += numeric.n
        return Number.isFinite(total) ? null : error('#¡NUM!')
      }
      for (const arg of args) {
        const failed = add(arg, false)
        if (failed !== null) return failed
      }
      return number(total === 0 ? 0 : total)
    },
  },
  /** COUNT: only numeric cells count; text and blanks are skipped. */
  COUNT: {
    arity: { min: 1 },
    syntax: 'COUNT(valor1; [valor2]; ...)',
    description: 'Cuenta las celdas que contienen números.',
    call: (args) => countValues(args),
  },
  /** COUNTA: every value that is not an empty cell, including text and errors. */
  COUNTA: {
    arity: { min: 1 },
    syntax: 'COUNTA(valor1; [valor2]; ...)',
    description: 'Cuenta las celdas que no están vacías.',
    call: (args) => countNonEmpty(args),
  },
  COUNTIF: {
    arity: 2,
    syntax: 'COUNTIF(rango; criterio)',
    description: 'Cuenta las celdas de un rango que cumplen un criterio.',
    call: (args) => {
      const pairs = buildCriteriaPairs(args, [0])
      return pairs.ok ? countMatching(pairs.pairs) : pairs.error
    },
  },
  COUNTIFS: {
    arity: { min: 2 },
    syntax: 'COUNTIFS(rango1; criterio1; [rango2; criterio2]; ...)',
    description: 'Cuenta las celdas que cumplen todos los criterios.',
    call: (args) => {
      if (args.length % 2 !== 0) return error('#¡VALOR!')
      const pairs = buildCriteriaPairs(args, alternatingRangeIndexes(0, args.length))
      return pairs.ok ? countMatching(pairs.pairs) : pairs.error
    },
  },
  SUMIF: {
    arity: { min: 2, max: 3 },
    syntax: 'SUMIF(rango; criterio; [rango_suma])',
    description: 'Suma las celdas de un rango que cumplen un criterio.',
    call: (args) => {
      const pairs = buildCriteriaPairs(args, [0])
      if (!pairs.ok) return pairs.error
      return aggregateMatching(args.length > 2 ? (args[2] ?? EMPTY) : (args[0] ?? EMPTY), pairs.pairs, 'sum')
    },
  },
  SUMIFS: {
    arity: { min: 3 },
    syntax: 'SUMIFS(rango_suma; rango1; criterio1; [rango2; criterio2]; ...)',
    description: 'Suma las celdas que cumplen todos los criterios.',
    call: (args) => {
      if (args.length % 2 === 0) return error('#¡VALOR!')
      const pairs = buildCriteriaPairs(args, alternatingRangeIndexes(1, args.length))
      if (!pairs.ok) return pairs.error
      return aggregateMatching(args[0] ?? EMPTY, pairs.pairs, 'sum')
    },
  },
  /** Google Sheets' FILTER: the rows of `rango` where every condition is true (or the columns,
   *  for a one-row range with conditions as wide as it). Conditions are evaluated element-wise,
   *  so `[n]>1` is one 1/0 per row. Only valid as a range argument of another function. */
  FILTER: {
    arity: { min: 2 },
    syntax: 'FILTER(rango; condición1; [condición2]; ...)',
    description: 'Devuelve las filas de un rango que cumplen todas las condiciones. Se usa como rango de otra función.',
    call: (args) => {
      const source = shapeOf(args[0] ?? EMPTY)
      if (source.kind === 'error') return source
      const conditions = args.slice(1).map(membersOf)
      const byRow = conditions.every((c) => c.length === source.rows)
      const byCol = !byRow && source.rows === 1 && conditions.every((c) => c.length === source.cols)
      if (!byRow && !byCol) return error('#¡VALOR!')
      const keep: boolean[] = []
      for (let i = 0; i < (byRow ? source.rows : source.cols); i++) {
        let all = true
        for (const condition of conditions) {
          const truth = truthiness(condition[i] ?? EMPTY)
          if (typeof truth !== 'boolean') return truth
          if (!truth) all = false
        }
        keep.push(all)
      }
      const out: FormulaValue[] = []
      for (let r = 0; r < source.rows; r++) {
        for (let c = 0; c < source.cols; c++) {
          if (keep[byRow ? r : c]) out.push(source.values[r * source.cols + c] ?? EMPTY)
        }
      }
      if (out.length === 0) return error('#N/D')
      return collection(out, byRow ? source.cols : out.length)
    },
  },
  /** Excel's TAKE: the first `filas` rows (last ones when negative) and, optionally, the first
   *  `columnas` columns. A count beyond the size takes everything; 0 is an error. */
  TAKE: {
    arity: { min: 2, max: 3 },
    syntax: 'TAKE(matriz; filas; [columnas])',
    description: 'Devuelve las primeras filas o columnas de un rango (las últimas si el número es negativo).',
    call: (args) => {
      const source = shapeOf(args[0] ?? EMPTY)
      if (source.kind === 'error') return source
      const count = (value: FormulaValue | undefined, size: number): [number, number] | FormulaValue => {
        if (value === undefined) return [0, size]
        const n = arithmetic([value], ([x]) => number(Math.trunc(x)))
        if (n.kind !== 'number') return n
        if (n.n === 0) return error('#¡VALOR!')
        const k = Math.min(Math.abs(n.n), size)
        return n.n > 0 ? [0, k] : [size - k, size]
      }
      const rows = count(args[1], source.rows)
      if (!Array.isArray(rows)) return rows
      const cols = count(args[2], source.cols)
      if (!Array.isArray(cols)) return cols
      const out: FormulaValue[] = []
      for (let r = rows[0]; r < rows[1]; r++) {
        for (let c = cols[0]; c < cols[1]; c++) out.push(source.values[r * source.cols + c] ?? EMPTY)
      }
      return out.length === 0 ? error('#¡VALOR!') : collection(out, cols[1] - cols[0])
    },
  },
  /** A column from row `desde` to row `hasta` (1-based, both included). An omitted (or 0 / "")
   *  `desde` starts at the first row and an omitted `hasta` ends at the formula's own row. The evaluator answers
   *  it (see `sliceRows`), since the default end needs the current row. */
  SLICE: {
    arity: { min: 1, max: 3 },
    syntax: 'SLICE(rango; [desde]; [hasta])',
    description: 'Devuelve las filas de un rango desde la fila «desde» hasta la fila «hasta». Se usa como rango de otra función.',
    call: () => error('#¡VALOR!'),
  },
  /** The rows of a column up to the current one. With `orden`, the rows whose `orden` is below the
   *  current row's, plus those tied with it up to the current row: everything until the same
   *  date, and within that date until this row. Answered by the evaluator (see `uptoRows`). */
  UPTO: {
    arity: { min: 1, max: 2 },
    syntax: 'UPTO(rango; [orden])',
    description: 'Devuelve las filas de un rango hasta la fila actual, o hasta su valor de «orden» (y entre los empatados, hasta la fila actual). Se usa como rango de otra función.',
    call: () => error('#¡VALOR!'),
  },
  /** Cost basis of each sale from the earlier purchases of the same asset, by FIFO, LIFO or the
   *  moving weighted average. Answered by the evaluator (see `costOf`): a row shows its own
   *  element of the column's result, and inside SUM it is the whole column. */
  COST: {
    arity: { min: 4, max: 7 },
    syntax: 'COST(activo; orden; cantidad; costo; [tipo_costo]; [tipo_resultado]; [método])',
    description: 'Devuelve el costo de cada venta (cantidad negativa) según las compras anteriores del mismo activo, por FIFO, LIFO o promedio ponderado (WAC).',
    call: () => error('#¡VALOR!'),
  },
  /** Excel's ROWS: how many rows a range (or FILTER/TAKE result) has. */
  ROWS: {
    arity: 1,
    syntax: 'ROWS(rango)',
    description: 'Devuelve el número de filas de un rango.',
    call: (args) => {
      const source = shapeOf(args[0] ?? EMPTY)
      return source.kind === 'error' ? source : number(source.rows)
    },
  },
  /** Excel's ROW: the row number of a reference, or of the formula's own row without one. The
   *  evaluator answers it from the reference itself (see `evaluateWithContext`), not its value. */
  ROW: {
    arity: { min: 0, max: 1 },
    syntax: 'ROW([referencia])',
    description: 'Devuelve el número de fila de una referencia, o de la fila actual.',
    call: () => error('#¡VALOR!'),
  },
  AVERAGE: {
    arity: { min: 1 },
    syntax: 'AVERAGE(valor1; [valor2]; ...)',
    description: 'Devuelve el promedio de los números de un rango o lista de celdas.',
    call: (args) => {
      const numbers = collectNumbers(args)
      if (!numbers.ok) return error(numbers.error)
      if (numbers.values.length === 0) return error('#¡DIV/0!')
      return finite(numbers.values.reduce((total, n) => total + n, 0) / numbers.values.length)
    },
  },
  AVERAGEIF: {
    arity: { min: 2, max: 3 },
    syntax: 'AVERAGEIF(rango; criterio; [rango_promedio])',
    description: 'Devuelve el promedio de las celdas de un rango que cumplen un criterio.',
    call: (args) => {
      const pairs = buildCriteriaPairs(args, [0])
      if (!pairs.ok) return pairs.error
      return aggregateMatching(args.length > 2 ? (args[2] ?? EMPTY) : (args[0] ?? EMPTY), pairs.pairs, 'average')
    },
  },
  AVERAGEIFS: {
    arity: { min: 3 },
    syntax: 'AVERAGEIFS(rango_promedio; rango1; criterio1; [rango2; criterio2]; ...)',
    description: 'Devuelve el promedio de las celdas que cumplen todos los criterios.',
    call: (args) => {
      if (args.length % 2 === 0) return error('#¡VALOR!')
      const pairs = buildCriteriaPairs(args, alternatingRangeIndexes(1, args.length))
      if (!pairs.ok) return pairs.error
      return aggregateMatching(args[0] ?? EMPTY, pairs.pairs, 'average')
    },
  },
  MAX: {
    arity: { min: 1 },
    syntax: 'MAX(valor1; [valor2]; ...)',
    description: 'Devuelve el número más grande de un rango o lista de celdas.',
    call: (args) => {
      const numbers = collectNumbers(args)
      if (!numbers.ok) return error(numbers.error)
      return numbers.values.length === 0 ? number(0) : finite(Math.max(...numbers.values))
    },
  },
  MIN: {
    arity: { min: 1 },
    syntax: 'MIN(valor1; [valor2]; ...)',
    description: 'Devuelve el número más pequeño de un rango o lista de celdas.',
    call: (args) => {
      const numbers = collectNumbers(args)
      if (!numbers.ok) return error(numbers.error)
      return numbers.values.length === 0 ? number(0) : finite(Math.min(...numbers.values))
    },
  },
  MAXIF: {
    arity: { min: 2, max: 3 },
    syntax: 'MAXIF(rango; criterio; [rango_máximo])',
    description: 'Devuelve el número más grande de un rango que cumple un criterio.',
    call: (args) => {
      const pairs = buildCriteriaPairs(args, [0])
      if (!pairs.ok) return pairs.error
      return aggregateMatching(args.length > 2 ? (args[2] ?? EMPTY) : (args[0] ?? EMPTY), pairs.pairs, 'max')
    },
  },
  MINIF: {
    arity: { min: 2, max: 3 },
    syntax: 'MINIF(rango; criterio; [rango_mínimo])',
    description: 'Devuelve el número más pequeño de un rango que cumple un criterio.',
    call: (args) => {
      const pairs = buildCriteriaPairs(args, [0])
      if (!pairs.ok) return pairs.error
      return aggregateMatching(args.length > 2 ? (args[2] ?? EMPTY) : (args[0] ?? EMPTY), pairs.pairs, 'min')
    },
  },
  MAXIFS: {
    arity: { min: 3 },
    syntax: 'MAXIFS(rango_máximo; rango1; criterio1; [rango2; criterio2]; ...)',
    description: 'Devuelve el número más grande de las celdas que cumplen todos los criterios.',
    call: (args) => {
      if (args.length % 2 === 0) return error('#¡VALOR!')
      const pairs = buildCriteriaPairs(args, alternatingRangeIndexes(1, args.length))
      if (!pairs.ok) return pairs.error
      return aggregateMatching(args[0] ?? EMPTY, pairs.pairs, 'max')
    },
  },
  MINIFS: {
    arity: { min: 3 },
    syntax: 'MINIFS(rango_mínimo; rango1; criterio1; [rango2; criterio2]; ...)',
    description: 'Devuelve el número más pequeño de las celdas que cumplen todos los criterios.',
    call: (args) => {
      if (args.length % 2 === 0) return error('#¡VALOR!')
      const pairs = buildCriteriaPairs(args, alternatingRangeIndexes(1, args.length))
      if (!pairs.ok) return pairs.error
      return aggregateMatching(args[0] ?? EMPTY, pairs.pairs, 'min')
    },
  },
  /** Excel's IF. A missing `si_falso` yields 0 as Excel's FALSE would; this grammar has no
   *  booleans, so 0 stands in for it. */
  IF: {
    arity: { min: 2, max: 3 },
    syntax: 'IF(condición; si_verdadero; [si_falso])',
    description: 'Devuelve un valor si la condición es verdadera y otro si es falsa.',
    call: (args) => {
      const test = truthiness(args[0] ?? EMPTY)
      if (typeof test !== 'boolean') return test
      return test ? (args[1] ?? EMPTY) : args.length > 2 ? (args[2] ?? EMPTY) : number(0)
    },
  },
  /** Excel's IFS: the first true condition wins; none true is `#N/A`. */
  IFS: {
    arity: { min: 2 },
    syntax: 'IFS(condición1; valor1; [condición2; valor2]; ...)',
    description: 'Devuelve el valor de la primera condición verdadera.',
    call: (args) => {
      if (args.length % 2 !== 0) return error('#N/A')
      for (let i = 0; i < args.length; i += 2) {
        const test = truthiness(args[i] ?? EMPTY)
        if (typeof test !== 'boolean') return test
        if (test) return args[i + 1] ?? EMPTY
      }
      return error('#N/A')
    },
  },
  /** With no boolean type, TRUE() and FALSE() are 1 and 0 — what Excel's booleans become in
   *  arithmetic, and exactly what IF/IFS read as true and false. */
  TRUE: {
    arity: 0,
    syntax: 'TRUE()',
    description: 'Devuelve el valor lógico VERDADERO (1).',
    call: () => number(1),
  },
  FALSE: {
    arity: 0,
    syntax: 'FALSE()',
    description: 'Devuelve el valor lógico FALSO (0).',
    call: () => number(0),
  },
  /** An empty value, as an unfilled cell is: `IF([A]>0;[A];NULL())` leaves the cell blank. */
  NULL: {
    arity: 0,
    syntax: 'NULL()',
    description: 'Devuelve un valor vacío, como una celda sin completar.',
    call: () => EMPTY,
  },
  /** Excel's TODAY: the local date as a whole day serial (no time part), so it compares and
   *  formats like any date cell. */
  TODAY: {
    arity: 0,
    syntax: 'TODAY()',
    description: 'Devuelve la fecha de hoy.',
    call: () => {
      const now = new Date()
      return number(dateToSerial(new Date(now.getFullYear(), now.getMonth(), now.getDate())))
    },
  },
  /** Excel's DATE: year, month and day as a date serial. Months and days past their range roll
   *  over (DATE(2026; 13; 1) is 1 Jan 2027); years 0–1899 count from 1900, as in Excel. */
  DATE: {
    arity: 3,
    syntax: 'DATE(año; mes; día)',
    description: 'Devuelve la fecha formada por el año, el mes y el día.',
    call: (args) =>
      arithmetic([args[0] ?? EMPTY, args[1] ?? EMPTY, args[2] ?? EMPTY], ([y, m, d]) => {
        const year = Math.trunc(y) < 1900 ? Math.trunc(y) + 1900 : Math.trunc(y)
        if (year < 1900 || year > 9999) return error('#¡NUM!')
        const date = new Date(year, Math.trunc(m) - 1, Math.trunc(d))
        const serial = dateToSerial(date)
        return serial < 0 ? error('#¡NUM!') : number(serial)
      }),
  },
  /**
   * The exchange rate of a pair ("USDARS": ARS per 1 USD) from the Numtable library's Exchange
   * rates document, on a date (default today; a day without a quote takes the latest earlier
   * one). The market (oficial, mep, blue) and quote (buy, sell, avg) only apply when ARS is one
   * side: they default to MEP and sell. #N/D until the history has loaded or with no quote.
   */
  FXRATE: {
    arity: { min: 1, max: 4 },
    syntax: 'FXRATE(monedas; [fecha]; [mercado]; [cotización])',
    description: 'Devuelve el tipo de cambio de un par de monedas en una fecha.',
    call: (args) => {
      for (const arg of args) if (arg.kind === 'error') return arg
      const pair = args[0]
      if (pair?.kind !== 'text') return error('#¡VALOR!')
      const code = pair.s.trim().toUpperCase().replace(/[^A-Z]/g, '')
      if (code.length !== 6) return error('#¡VALOR!')
      let day: string
      const dateArg = args[1] ?? EMPTY
      if (dateArg.kind === 'empty') {
        const now = new Date()
        day = isoDay(now)
      } else {
        const serial = numericMember(dateArg, false)
        if (typeof serial !== 'number') return error(serial ?? '#¡VALOR!')
        day = isoDay(serialToDate(Math.floor(serial)))
      }
      const option = (value: FormulaValue | undefined): string | null | undefined => {
        if (!value || value.kind === 'empty') return undefined
        return value.kind === 'text' ? value.s.trim().toLowerCase() : null
      }
      const marketText = option(args[2])
      const sideText = option(args[3])
      if (marketText === null || sideText === null) return error('#¡VALOR!')
      const market = marketText === undefined ? 'mep' : marketText === 'official' ? 'oficial' : marketText
      const side = sideText === undefined ? 'sell' : sideText === 'average' ? 'avg' : sideText
      if (!['oficial', 'mep', 'blue'].includes(market) || !['buy', 'sell', 'avg'].includes(side)) return error('#¡VALOR!')
      const rate = fxRateProvider?.({ from: code.slice(0, 3), to: code.slice(3), market, side }, day)
      return rate == null || !Number.isFinite(rate) ? error('#N/D') : number(Math.round(rate * 1e6) / 1e6)
    },
  },
  /**
   * Google Sheets' LET(nombre1; valor1; [nombre2; valor2; ...]; expresión). Parsing and evaluation
   * are special-cased (names bind left to right and are lazy); this entry exists for the tokenizer,
   * the arity check and the autocomplete list. `call` is never reached.
   */
  LET: {
    arity: { min: 3 },
    syntax: 'LET(nombre1; valor1; [nombre2; valor2; ...]; expresión)',
    description: 'Asigna nombres a valores y calcula la expresión usando esos nombres.',
    call: () => error('#¡VALOR!'),
  },
  /** Excel's IFERROR: any error in `valor` is replaced by `si_error`. */
  IFERROR: {
    arity: 2,
    syntax: 'IFERROR(valor; si_error)',
    description: 'Devuelve si_error cuando valor es un error; si no, devuelve valor.',
    call: (args) => {
      const value = args[0] ?? EMPTY
      return value.kind === 'error' ? (args[1] ?? EMPTY) : value
    },
  },
  /** Excel's NOT: 1 when the condition is false, 0 when it is true (no boolean type, as in IF). */
  NOT: {
    arity: 1,
    syntax: 'NOT(condición)',
    description: 'Invierte el valor lógico: devuelve 1 si la condición es falsa y 0 si es verdadera.',
    call: (args) => {
      const test = truthiness(args[0] ?? EMPTY)
      if (typeof test !== 'boolean') return test
      return number(test ? 0 : 1)
    },
  },
  /** Excel's AND: 1 when every condition is true. Inside ranges text and blanks are skipped;
   *  with no logical value at all the result is `#¡VALOR!`, as in Excel. */
  AND: {
    arity: { min: 1 },
    syntax: 'AND(condición1; [condición2]; ...)',
    description: 'Devuelve 1 si todas las condiciones son verdaderas y 0 si alguna es falsa.',
    call: (args) => logicalFold(args, 'and'),
  },
  /** Excel's OR: 1 when at least one condition is true. Same coercion rules as AND. */
  OR: {
    arity: { min: 1 },
    syntax: 'OR(condición1; [condición2]; ...)',
    description: 'Devuelve 1 si alguna condición es verdadera y 0 si todas son falsas.',
    call: (args) => logicalFold(args, 'or'),
  },
  /** Excel's ROUND: half away from zero; negative digits round to the left of the decimal point. */
  ROUND: {
    arity: 2,
    syntax: 'ROUND(número; núm_decimales)',
    description: 'Redondea un número a la cantidad de decimales indicada.',
    call: (args) => arithmetic([args[0] ?? EMPTY, args[1] ?? EMPTY], ([n, d]) => roundTo(n, d, 'half')),
  },
  /** Excel's ROUNDUP: away from zero. */
  ROUNDUP: {
    arity: 2,
    syntax: 'ROUNDUP(número; núm_decimales)',
    description: 'Redondea un número hacia arriba, alejándolo de cero.',
    call: (args) => arithmetic([args[0] ?? EMPTY, args[1] ?? EMPTY], ([n, d]) => roundTo(n, d, 'up')),
  },
  /** Excel's ROUNDDOWN: toward zero. */
  ROUNDDOWN: {
    arity: 2,
    syntax: 'ROUNDDOWN(número; núm_decimales)',
    description: 'Redondea un número hacia abajo, acercándolo a cero.',
    call: (args) => arithmetic([args[0] ?? EMPTY, args[1] ?? EMPTY], ([n, d]) => roundTo(n, d, 'down')),
  },
  /** Excel's INT: rounds DOWN to the integer below, so INT(-1,5) is -2 (not a truncation). */
  INT: {
    arity: 1,
    syntax: 'INT(número)',
    description: 'Redondea un número hacia abajo hasta el entero más próximo.',
    call: (args) => arithmetic([args[0] ?? EMPTY], ([n]) => number(Math.floor(n) || 0)),
  },
  /** Excel's MROUND: nearest multiple, half away from zero. Signs that differ are `#¡NUM!`. */
  MROUND: {
    arity: 2,
    syntax: 'MROUND(número; múltiplo)',
    description: 'Redondea un número al múltiplo más cercano.',
    call: (args) =>
      arithmetic([args[0] ?? EMPTY, args[1] ?? EMPTY], ([n, m]) => {
        if (m === 0 || n === 0) return number(0)
        if (Math.sign(n) !== Math.sign(m)) return error('#¡NUM!')
        const q = Number((n / m).toPrecision(15))
        const result = Math.sign(q) * Math.round(Math.abs(q)) * m
        return number(Number(result.toPrecision(15)) || 0)
      }),
  },
  /** Excel's DAY / MONTH / YEAR: parts of a date serial (or of text that reads as a date). */
  DAY: {
    arity: 1,
    syntax: 'DAY(fecha)',
    description: 'Devuelve el día del mes (1 a 31) de una fecha.',
    call: (args) => datePart(args[0] ?? EMPTY, (d) => d.getDate()),
  },
  MONTH: {
    arity: 1,
    syntax: 'MONTH(fecha)',
    description: 'Devuelve el mes (1 a 12) de una fecha.',
    call: (args) => datePart(args[0] ?? EMPTY, (d) => d.getMonth() + 1),
  },
  YEAR: {
    arity: 1,
    syntax: 'YEAR(fecha)',
    description: 'Devuelve el año de una fecha.',
    call: (args) => datePart(args[0] ?? EMPTY, (d) => d.getFullYear()),
  },
  /** Excel's DATEDIF: the difference between two dates in "Y", "M", "D", "MD", "YM" or "YD".
   *  A start after the end is `#¡NUM!`, as in Excel. */
  DATEDIF: {
    arity: 3,
    syntax: 'DATEDIF(fecha_inicial; fecha_final; unidad)',
    description: 'Calcula la diferencia entre dos fechas en años, meses o días.',
    call: (args) => {
      const unitArg = args[2] ?? EMPTY
      if (unitArg.kind === 'error') return unitArg
      return arithmetic([args[0] ?? EMPTY, args[1] ?? EMPTY], ([a, b]) => {
        if (unitArg.kind !== 'text') return error('#¡NUM!')
        const start = Math.floor(a)
        const end = Math.floor(b)
        if (start < 0 || start > end) return error('#¡NUM!')
        const s = serialToDate(start)
        const e = serialToDate(end)
        const months =
          (e.getFullYear() - s.getFullYear()) * 12 + e.getMonth() - s.getMonth() - (e.getDate() < s.getDate() ? 1 : 0)
        switch (unitArg.s.trim().toUpperCase()) {
          case 'D':
            return number(end - start)
          case 'M':
            return number(months)
          case 'Y':
            return number(Math.floor(months / 12))
          case 'YM':
            return number(months % 12)
          case 'MD': {
            if (e.getDate() >= s.getDate()) return number(e.getDate() - s.getDate())
            return number(end - dateToSerial(new Date(e.getFullYear(), e.getMonth() - 1, s.getDate())))
          }
          case 'YD': {
            let shifted = dateToSerial(new Date(e.getFullYear(), s.getMonth(), s.getDate()))
            if (shifted > end) shifted = dateToSerial(new Date(e.getFullYear() - 1, s.getMonth(), s.getDate()))
            return number(end - shifted)
          }
          default:
            return error('#¡NUM!')
        }
      })
    },
  },
  /** The IS* family never propagates an error: it answers 1 or 0 for any value, as in Excel. */
  ISNUMBER: {
    arity: 1,
    syntax: 'ISNUMBER(valor)',
    description: 'Devuelve 1 si el valor es un número y 0 si no.',
    call: (args) => number(args[0]?.kind === 'number' ? 1 : 0),
  },
  ISTEXT: {
    arity: 1,
    syntax: 'ISTEXT(valor)',
    description: 'Devuelve 1 si el valor es texto y 0 si no.',
    call: (args) => number(args[0]?.kind === 'text' ? 1 : 0),
  },
  /** Dates reach formulas as day serials, indistinguishable from plain numbers, so only text
   *  that reads as a date counts (ISO, dd/mm/yyyy, dd/mm — what `fromCell` accepts). */
  ISDATE: {
    arity: 1,
    syntax: 'ISDATE(valor)',
    description: 'Devuelve 1 si el valor es un texto que se lee como fecha y 0 si no.',
    call: (args) => {
      const value = args[0] ?? EMPTY
      return number(value.kind === 'text' && parseGridDate(value.s) !== null ? 1 : 0)
    },
  },
  /** Excel's ISBLANK: only an empty value counts; `""` is text, not blank. */
  ISBLANK: {
    arity: 1,
    syntax: 'ISBLANK(valor)',
    description: 'Devuelve 1 si el valor está vacío y 0 si no.',
    call: (args) => number((args[0] ?? EMPTY).kind === 'empty' ? 1 : 0),
  },
  /** Excel's ISERROR: 1 for any error code. */
  ISERROR: {
    arity: 1,
    syntax: 'ISERROR(valor)',
    description: 'Devuelve 1 si el valor es un error y 0 si no.',
    call: (args) => number(args[0]?.kind === 'error' ? 1 : 0),
  },
  /** The test that matches NULL(): the same as ISBLANK. */
  ISNULL: {
    arity: 1,
    syntax: 'ISNULL(valor)',
    description: 'Devuelve 1 si el valor está vacío (como NULL()) y 0 si no.',
    call: (args) => number((args[0] ?? EMPTY).kind === 'empty' ? 1 : 0),
  },
  /**
   * XLOOKUP(search_key, lookup_range, result_range, [missing_value], [match_mode], [search_mode])
   * Cross lookup function. Returns the values in result_range at the position where a match was found in lookup_range.
   */
  XLOOKUP: {
    arity: { min: 3, max: 6 },
    syntax: 'XLOOKUP(search_key; lookup_range; result_range; [missing_value]; [match_mode]; [search_mode])',
    description: 'Búsqueda cruzada. Devuelve los valores de result_range en la posición donde se encontró coincidencia en lookup_range.',
    call: (args) => {
      if (args[0]?.kind === 'error') return args[0]
      if (args[1]?.kind === 'error') return args[1]
      if (args[2]?.kind === 'error') return args[2]

      const searchKey = args[0] ?? EMPTY
      const lookupArg = args[1] ?? EMPTY
      const resultArg = args[2] ?? EMPTY

      const getValues = (v: FormulaValue): readonly FormulaValue[] =>
        v.kind === 'collection' ? v.values : [v]

      const lookupValues = getValues(lookupArg)
      const resultValues = getValues(resultArg)

      const missingValue = args.length > 3 && args[3]?.kind !== 'empty' ? args[3] : undefined

      let matchMode = 0
      if (args.length > 4 && args[4] !== undefined && args[4].kind !== 'empty') {
        const modeVal = arithmetic([args[4]], ([n]) => number(n))
        if (modeVal.kind === 'error') return modeVal
        if (modeVal.kind === 'number') matchMode = Math.trunc(modeVal.n)
      }
      if (![0, 1, -1, 2].includes(matchMode)) return error('#¡VALOR!')

      let searchMode = 1
      if (args.length > 5 && args[5] !== undefined && args[5].kind !== 'empty') {
        const modeVal = arithmetic([args[5]], ([n]) => number(n))
        if (modeVal.kind === 'error') return modeVal
        if (modeVal.kind === 'number') searchMode = Math.trunc(modeVal.n)
      }
      if (![1, -1, 2, -2].includes(searchMode)) return error('#¡VALOR!')

      const n = lookupValues.length
      if (n === 0) return missingValue ?? error('#N/A')

      let foundIndex = -1

      if (searchMode === 1 || searchMode === -1) {
        const step = searchMode === 1 ? 1 : -1
        const start = searchMode === 1 ? 0 : n - 1
        const end = searchMode === 1 ? n : -1

        let bestIndex = -1
        let bestVal: FormulaValue | undefined = undefined

        for (let i = start; i !== end; i += step) {
          const item = lookupValues[i]!
          const res = compareFormulaValues(searchKey, item, matchMode)
          if (res.match) {
            foundIndex = i
            break
          }
          if (matchMode === -1 && res.cmp !== undefined && res.cmp > 0) {
            // searchKey > item (item is smaller than searchKey)
            if (
              bestVal === undefined ||
              compareFormulaValues(item, bestVal, 0).cmp! > 0
            ) {
              bestIndex = i
              bestVal = item
            }
          } else if (matchMode === 1 && res.cmp !== undefined && res.cmp < 0) {
            // searchKey < item (item is larger than searchKey)
            if (
              bestVal === undefined ||
              compareFormulaValues(item, bestVal, 0).cmp! < 0
            ) {
              bestIndex = i
              bestVal = item
            }
          }
        }
        if (foundIndex === -1 && bestIndex !== -1) {
          foundIndex = bestIndex
        }
      } else if (searchMode === 2 || searchMode === -2) {
        let low = 0
        let high = n - 1
        let bestIndex = -1

        while (low <= high) {
          const mid = Math.floor((low + high) / 2)
          const item = lookupValues[mid]!
          const res = compareFormulaValues(searchKey, item, matchMode)

          if (res.match) {
            foundIndex = mid
            break
          }

          if (searchMode === 2) {
            // Ascending order
            if (res.cmp !== undefined) {
              if (res.cmp < 0) {
                if (matchMode === 1) bestIndex = mid
                high = mid - 1
              } else {
                if (matchMode === -1) bestIndex = mid
                low = mid + 1
              }
            } else {
              low++
            }
          } else {
            // Descending order
            if (res.cmp !== undefined) {
              if (res.cmp < 0) {
                if (matchMode === 1) bestIndex = mid
                low = mid + 1
              } else {
                if (matchMode === -1) bestIndex = mid
                high = mid - 1
              }
            } else {
              low++
            }
          }
        }
        if (foundIndex === -1 && bestIndex !== -1) {
          foundIndex = bestIndex
        }
      }

      if (foundIndex === -1) {
        return missingValue ?? error('#N/A')
      }

      const rLen = resultValues.length
      if (rLen === 0) return missingValue ?? error('#N/A')

      const k = Math.max(1, Math.floor(rLen / n))
      const startRes = foundIndex * k
      if (startRes >= rLen) return error('#¡VALOR!')

      if (k === 1) {
        return resultValues[startRes]!
      } else {
        const slice = resultValues.slice(startRes, startRes + k)
        return collection(slice)
      }
    },
  },
  /**
   * TEXT: converts a number or date to text according to a specified format pattern or global format name.
   */
  TEXT: {
    arity: 2,
    syntax: 'TEXT(número; formato)',
    description: 'Convierte un número o fecha en texto con el formato especificado o formato global.',
    call: (args) => {
      if (args[0]?.kind === 'error') return args[0]
      if (args[1]?.kind === 'error') return args[1]
      const val = args[0] ?? EMPTY
      const fmt = args[1] ?? EMPTY
      if (val.kind === 'collection' || fmt.kind === 'collection') return error('#¡VALOR!')

      let pattern = ''
      if (fmt.kind === 'text') {
        pattern = resolveGlobalFormatPattern(fmt.s)
      } else if (fmt.kind === 'number') {
        pattern = String(fmt.n)
      } else if (fmt.kind === 'empty') {
        pattern = ''
      }

      if (val.kind === 'empty') {
        const isDate = esFormatoFecha(pattern)
        return isDate ? text(formatearExcel(serialToDate(0), pattern)) : text(formatearExcel(0, pattern))
      }

      if (val.kind === 'number') {
        const isDate = esFormatoFecha(pattern)
        return isDate ? text(formatearExcel(serialToDate(val.n), pattern)) : text(formatearExcel(val.n, pattern))
      }

      if (val.kind === 'text') {
        if (val.s.trim() === '') {
          return text('')
        }
        const d = parseGridDate(val.s)
        if (d !== null) {
          const isDate = esFormatoFecha(pattern)
          return isDate ? text(formatearExcel(d, pattern)) : text(formatearExcel(dateToSerial(d), pattern))
        }
        const n = numericText(val.s)
        if (n !== null) {
          const isDate = esFormatoFecha(pattern)
          return isDate ? text(formatearExcel(serialToDate(n), pattern)) : text(formatearExcel(n, pattern))
        }
        return error('#¡VALOR!')
      }

      return error('#¡VALOR!')
    },
  },
}

export const FUNCTION_LIST: readonly FunctionMetadata[] = Object.entries(FUNCTIONS).map(
  ([name, spec]) => ({
    name,
    syntax: spec.syntax ?? `${name}()`,
    description: spec.description ?? '',
  }),
)

// ── Column scope ──────────────────────────────────────────────────────────────────────────────

/**
 * Translates between what the user sees (`[Precio lista]`) and what gets stored
 * (`[precio_lista]`). Storing the `field` is what makes a formula survive a header rename:
 * renaming only writes `encabezados_grilla`, and the stored text never mentions the name.
 */
/**
 * Arguments that take one of a fixed set of words, by function and 0-based argument index, for the
 * formula editor to offer as a list. They must be the words the evaluator accepts (`costOf`), in
 * the case they are written in; costBasis.test.ts checks every one evaluates.
 */
export const KEYWORD_ARGUMENTS: Readonly<Record<string, Readonly<Record<number, readonly string[]>>>> = {
  COST: { 4: ['Unit', 'Total'], 5: ['Unit', 'Total'], 6: ['FIFO', 'LIFO', 'WAC'] },
}

export interface ColumnScope {
  fieldOfName: (name: string) => { ok: true; field: string } | { ok: false; reason: 'unknown' | 'ambiguous' }
  nameOfField: (field: string) => string | undefined
}

export interface FormulaSheetScope {
  id: string
  name: string
  columns: ColumnScope
}

/** Optional workbook/row identity used by cell formulas. Existing column-formula callers can
 * keep passing a plain ColumnScope. Row numbers are deliberately resolved at parse time and
 * stable IDs are kept in the AST/canonical text. */
export interface CellFormulaScope extends ColumnScope {
  currentSheetId?: string
  rowIdAt?: (oneBasedIndex: number) => string | undefined
  rowIndexOf?: (rowId: string) => number | undefined
  sheetOfName?: (name: string) => FormulaSheetScope | undefined | 'ambiguous'
  sheetNameOfId?: (sheetId: string) => string | undefined
  columnsOfSheet?: (sheetId: string) => ColumnScope | undefined
  /** Other documents. Absent while nothing cross-document is loaded, which is the normal case
   *  today: the prefix then resolves to an opaque id and evaluation yields `#¡REF!`. */
  documentOfName?: (name: string) => { id: string; name: string } | undefined | 'ambiguous'
  documentNameOfId?: (documentId: string) => string | undefined
}

/**
 * Case- and whitespace-insensitive, but accent-SENSITIVE: "Año" and "Ano" are different columns
 * and the user expects to type the accent.
 *
 * Two Unicode details earn their place. NFC composition means a name pasted in decomposed form
 * (`e` + combining acute, which is what macOS filenames and some exports produce) matches the same
 * name typed normally. And the variation selector is dropped, so `📊` and `📊️` — the same emoji
 * with and without an explicit emoji-presentation request, indistinguishable on screen — are the
 * same name. The zero-width joiner is deliberately KEPT: `👨‍👩‍👧` is one family, not three people.
 */
export function normaliseColumnName(name: string): string {
  return name
    .normalize('NFC')
    .trim()
    .replace(/\uFE0F/g, '')
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('es')
}

/** Sheets and documents fold exactly like columns — same trimming, same emoji handling, same
 *  accent sensitivity. One function, so the three can never drift apart and leave the editor
 *  offering a name the parser then fails to resolve. */
export const normaliseContainerName = normaliseColumnName

/** Builds a scope from visible-name/field pairs. A name claimed by more than one field
 *  resolves to `ambiguous` rather than silently picking one. */
export function columnScope(columns: readonly { field: string; name: string }[]): ColumnScope {
  const nameByField = new Map<string, string>()
  const fieldByName = new Map<string, string | null>() // null marks an ambiguous name
  const fieldBySpacelessName = new Map<string, string | null>()

  for (const { field, name } of columns) {
    // Headers may wrap with line breaks; a formula spells the name on one line.
    if (!nameByField.has(field)) nameByField.set(field, name.replace(/\s*\n\s*/g, ' ').trim())
    const key = normaliseColumnName(name)
    fieldByName.set(key, fieldByName.has(key) ? null : field)
    const spacelessKey = key.replace(/\s+/g, '')
    fieldBySpacelessName.set(spacelessKey, fieldBySpacelessName.has(spacelessKey) ? null : field)
  }

  return {
    nameOfField: (field) => nameByField.get(field),
    fieldOfName: (name) => {
      const norm = normaliseColumnName(name)
      const found = fieldByName.get(norm)
      if (found !== undefined) {
        if (found === null) return { ok: false, reason: 'ambiguous' }
        return { ok: true, field: found }
      }
      const spacelessNorm = norm.replace(/\s+/g, '')
      const spacelessFound = fieldBySpacelessName.get(spacelessNorm)
      if (spacelessFound !== undefined) {
        if (spacelessFound === null) return { ok: false, reason: 'ambiguous' }
        return { ok: true, field: spacelessFound }
      }
      return { ok: false, reason: 'unknown' }
    },
  }
}

const EMPTY_COLUMN_SCOPE: ColumnScope = {
  fieldOfName: () => ({ ok: false, reason: 'unknown' }),
  nameOfField: () => undefined,
}

// ── Errors ────────────────────────────────────────────────────────────────────────────────────

/**
 * Stable identity for the diagnostics the UI needs to translate. Most parser messages predate
 * this and carry only `message`; the two that the syntax change introduced are user-facing
 * enough to be worth localising, so they also carry a `code` and the pieces needed to build the
 * message in another language. `message` always holds a usable Spanish fallback, which is what
 * keeps this module free of any i18n import.
 */
export type FormulaDiagnosticCode = 'legacy-sheet-separator' | 'stray-dot-before-bracket'

/** Spanish message plus the range to underline. `start === end` marks a caret. */
export interface FormulaSyntaxError extends Span {
  message: string
  code?: FormulaDiagnosticCode
  /** Interpolation values for `code`, e.g. `{ canonical: "'Hoja 1'[Total]" }`. */
  data?: Readonly<Record<string, string>>
}

/** A diagnostic that does NOT stop the parse. Same shape as the error on purpose: the UI renders
 *  both through one path and only the tone differs. */
export interface FormulaWarning extends Span {
  message: string
  code: FormulaDiagnosticCode
  data?: Readonly<Record<string, string>>
}

export type ParseResult =
  | {
      ok: true
      ast: AstNode
      refs: readonly string[]
      references: readonly FormulaReference[]
      /** Accepted, but worth telling the user about — a stray dot before `[`, say. Serializing
       *  the returned AST writes the canonical form back, so saving is the fix. */
      warnings: readonly FormulaWarning[]
    }
  | { ok: false; error: FormulaSyntaxError }

/** Typed dependencies for cell formulas. `ParseResult.refs` remains as the legacy field-only
 * list used by the calculated-column graph. */
export function formulaReferences(ast: AstNode): readonly FormulaReference[] {
  const found: FormulaReference[] = []
  const visit = (node: AstNode): void => {
    switch (node.kind) {
      case 'ref':
        found.push({
          field: node.field,
          row: node.row ?? 'current',
          ...(node.sheetId === undefined ? {} : { sheetId: node.sheetId }),
          ...(node.documentId === undefined ? {} : { documentId: node.documentId }),
        })
        return
      case 'range':
        visit(node.from)
        visit(node.to)
        return
      case 'call':
        node.args.forEach(visit)
        return
      case 'binary':
        visit(node.left)
        visit(node.right)
        return
      case 'unary':
      case 'percent':
      case 'group':
        visit(node.arg)
        return
      case 'number':
      case 'text':
      case 'name':
      // Unparsed text has no discoverable dependencies: it contributes nothing to the graph.
      case 'raw':
        return
    }
  }
  visit(ast)
  const unique = new Map(found.map((ref) => [JSON.stringify(ref), ref]))
  return [...unique.values()]
}

/** Thrown inside the parser and caught by the public entry points, so callers get a result
 *  object and the helpers stay small (no error accumulator threaded through every function). */
class ParseFailure extends Error {
  constructor(readonly detail: FormulaSyntaxError) {
    super(detail.message)
  }
}

const fail = (
  message: string,
  start: number,
  end: number = start,
  extra?: { code: FormulaDiagnosticCode; data?: Readonly<Record<string, string>> },
): never => {
  throw new ParseFailure({ message, start, end, ...extra })
}

// ── Tokenizer ─────────────────────────────────────────────────────────────────────────────────

type TokenKind = 'number' | 'string' | 'func' | 'containerName' | 'dot' | 'ref' | 'operator' | 'percent' | 'colon' | 'open' | 'close' | 'argSep' | 'end'

interface Token extends Span {
  kind: TokenKind
  text: string
  nl?: boolean
  /** `number` only. */
  n?: number
  /** `ref` only: the bracket contents, with `]]` already unescaped. Also `string`: the literal's
   *  contents, with `""` already unescaped. Also `func`: the UPPER-CASE function name. Also
   *  `containerName`: the document/sheet name, with `''` already unescaped. */
  name?: string
  /** `containerName` only: whether the source wrapped it in apostrophes. A bare name is only
   *  legal because it needs no quoting, so this is what lets the lexer stay reversible. */
  quoted?: boolean
  /** `operator` only. */
  op?: BinaryOperator
  /** Visible row number, or canonical stable row ID. */
  row?: number | string
  currentRow?: boolean
}

const isDigit = (c: string): boolean => c >= '0' && c <= '9'

/**
 * The character at `i` as a WHOLE code point.
 *
 * Most emoji live outside the Basic Multilingual Plane and occupy two UTF-16 units, so `charAt`
 * returns half a surrogate pair — which matches no Unicode property and prints as `�`. Every
 * place the lexer inspects or reports "the next character" goes through this instead, and advances
 * by `.length` rather than by one.
 */
const codePointAt = (source: string, i: number): string => {
  const point = source.codePointAt(i)
  return point === undefined ? '' : String.fromCodePoint(point)
}

/**
 * What may appear in a name written WITHOUT apostrophes.
 *
 * Emoji are allowed, because a sheet called `📊Ventas` is an ordinary thing to want and there is
 * no ambiguity in it: no emoji collides with an operator, a bracket or a separator. The pieces
 * that only ever join a sequence — the zero-width joiner in `👨‍👩‍👧`, a skin-tone modifier, the
 * variation selector, the keycap mark — are continuation-only.
 *
 * A name still may not START with a digit, which is what keeps `1` from being read as a number.
 * Keycap emoji (`1️⃣` is `1` + VS16 + U+20E3) are therefore quoted like any other awkward name.
 *
 * These two are the single source of truth for "does this need quotes": `containerNameNeedsQuotes`
 * is defined from them, so the set of names the lexer accepts bare and the set the serializer
 * writes bare cannot drift apart.
 */
export const NAME_START = /[\p{L}_\p{Extended_Pictographic}\p{Regional_Indicator}]/u
export const NAME_PART = /[\p{L}\p{N}_\p{Extended_Pictographic}\p{Regional_Indicator}\p{Emoji_Modifier}\u200D\uFE0F\u20E3]/u

/**
 * The v1/v2 spelling `'Name'![Column]` rewritten to `'Name'[Column]`.
 *
 * A plain scanner rather than a parse, because it has to work on text that the CURRENT grammar
 * refuses — that is the whole point. It walks the same four literal forms the tokenizer knows
 * (`"..."`, `[...]` with its `@'row-id'` suffix, `'...'`) so a `!` inside a column name or a text
 * literal is left alone, and only the one directly after a closing apostrophe is dropped.
 *
 * Idempotent: after one pass no `!` follows an apostrophe, so a second pass changes nothing.
 */
export function rewriteLegacySeparators(source: string): string {
  let out = ''
  let i = 0
  while (i < source.length) {
    const c = source.charAt(i)

    if (c === '"') {
      const start = i
      i++
      while (i < source.length) {
        if (source.charAt(i) === '"') {
          if (source.charAt(i + 1) === '"') { i += 2; continue }
          i++
          break
        }
        i++
      }
      out += source.slice(start, i)
      continue
    }

    if (c === '[') {
      const start = i
      i++
      while (i < source.length) {
        if (source.charAt(i) === ']') {
          if (source.charAt(i + 1) === ']') { i += 2; continue }
          i++
          break
        }
        i++
      }
      // The row suffix: digits, or `@'row-id'` whose apostrophes are NOT a container name.
      while (i < source.length && isDigit(source.charAt(i))) i++
      if (source.slice(i, i + 2) === "@'") {
        i += 2
        while (i < source.length) {
          if (source.charAt(i) === "'") {
            if (source.charAt(i + 1) === "'") { i += 2; continue }
            i++
            break
          }
          i++
        }
      }
      out += source.slice(start, i)
      continue
    }

    if (c === "'") {
      const start = i
      i++
      let closed = false
      while (i < source.length) {
        if (source.charAt(i) === "'") {
          if (source.charAt(i + 1) === "'") { i += 2; continue }
          i++
          closed = true
          break
        }
        i++
      }
      out += source.slice(start, i)
      // This is the rewrite, and the only place anything is dropped.
      if (closed && source.charAt(i) === '!') i++
      continue
    }

    out += c
    i++
  }
  return out
}

function tokenize(source: string, locale: FormulaLocale): Token[] {
  const tokens: Token[] = []
  let i = 0
  let hadNewline = false
  while (i < source.length) {
    // A whole code point, so an emoji is one character here rather than two broken halves.
    const c = codePointAt(source, i)
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      if (c === '\n') hadNewline = true
      i++
      continue
    }
    const tokenStartNl = hadNewline
    hadNewline = false
    const start = i

    // `.` is the container separator ONLY directly after a container name; everywhere else it
    // falls through to the number branch below, which is what keeps `[Amount]*1.5` working when
    // the decimal separator is also `.`. Deciding on the PREVIOUS token rather than on the
    // locale is what makes the grammar mean the same thing for every user.
    if (c === '.' && tokens[tokens.length - 1]?.kind === 'containerName') {
      i++
      tokens.push({ kind: 'dot', text: '.', start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }

    if (isDigit(c) || c === locale.decimal) {
      while (i < source.length && isDigit(source.charAt(i))) i++
      if (source.charAt(i) === locale.decimal) {
        i++
        while (i < source.length && isDigit(source.charAt(i))) i++
      }
      const text = source.slice(start, i)
      // A lone separator ("," or ".") is not a number.
      if (!/\d/.test(text)) fail(`Carácter no admitido: «${text}».`, start, i)
      // The token carries the CANONICAL spelling, so a node always holds `.` as its decimal
      // separator no matter which locale it was parsed in. Only the separator character is
      // swapped, never the digits: that is what keeps `0.1+0.2` from becoming float noise.
      const canonicalText = locale.decimal === '.' ? text : text.replace(locale.decimal, '.')
      const n = Number(canonicalText)
      if (!Number.isFinite(n)) fail(`Número inválido: «${text}».`, start, i)
      tokens.push({ kind: 'number', text: canonicalText, n, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }

    if (c === '[') {
      i++
      let name = ''
      let currentRow = false
      if (source.charAt(i) === '@') {
        currentRow = true
        i++
      }
      let closed = false
      while (i < source.length) {
        if (source.charAt(i) === ']') {
          // `]]` is a literal `]` inside the name (Excel's own escape).
          if (source[i + 1] === ']') {
            name += ']'
            i += 2
            continue
          }
          i++
          closed = true
          break
        }
        name += source.charAt(i)
        i++
      }
      if (!closed) fail('Falta cerrar el corchete de la columna.', start, i)
      let row: number | string | undefined
      const digitsStart = i
      while (i < source.length && isDigit(source.charAt(i))) i++
      if (i > digitsStart) row = Number(source.slice(digitsStart, i))
      // Canonical stable identity: [field]@'row-id', escaping apostrophes by doubling them.
      if (!currentRow && source.slice(i, i + 2) === "@'") {
        i += 2
        let id = ''
        let idClosed = false
        while (i < source.length) {
          if (source.charAt(i) === "'") {
            if (source.charAt(i + 1) === "'") { id += "'"; i += 2; continue }
            i++
            idClosed = true
            break
          }
          id += source.charAt(i++)
        }
        if (!idClosed) fail('Falta cerrar el apóstrofe del identificador de fila.', start, i)
        row = id
      }
      tokens.push({
        kind: 'ref', text: source.slice(start, i), name, currentRow, start, end: i,
        ...(tokenStartNl ? { nl: true } : {}),
        ...(row === undefined ? {} : { row }),
      })
      continue
    }

    if (c === "'") {
      i++
      let name = ''
      let closed = false
      while (i < source.length) {
        if (source.charAt(i) === "'") {
          if (source.charAt(i + 1) === "'") { name += "'"; i += 2; continue }
          i++
          closed = true
          break
        }
        name += source.charAt(i++)
      }
      if (!closed) fail('Falta cerrar el apóstrofe del nombre de hoja.', start, i)
      tokens.push({ kind: 'containerName', text: source.slice(start, i), name, quoted: true, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }

    if (c === '"') {
      i++
      let value = ''
      let closed = false
      while (i < source.length) {
        if (source.charAt(i) === '"') {
          // `""` is a literal quote inside the text (Excel's own escape).
          if (source[i + 1] === '"') {
            value += '"'
            i += 2
            continue
          }
          i++
          closed = true
          break
        }
        value += source.charAt(i)
        i++
      }
      if (!closed) fail('Falta cerrar la comilla del texto.', start, i)
      tokens.push({ kind: 'string', text: source.slice(start, i), name: value, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }

    // The one character the new grammar deliberately refuses. Showing the rewritten formula is
    // more useful than naming the rule, so the diagnostic carries it.
    if (c === '!') {
      i++
      const suggestion = rewriteLegacySeparators(source).trim()
      fail(
        `La sintaxis cambió: «!» ya no separa la hoja de la columna. Escribí «${suggestion}».`,
        start,
        i,
        { code: 'legacy-sheet-separator', data: { suggestion } },
      )
    }

    if (c === '+' || c === '-' || c === '*' || c === '/' || c === '^' || c === '&') {
      i++
      tokens.push({ kind: 'operator', text: c, op: c, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }
    if (c === '=' || c === '<' || c === '>') {
      const pair = source.slice(i, i + 2)
      const op: ComparisonOperator = pair === '<>' || pair === '<=' || pair === '>=' ? pair : c
      i += op.length
      tokens.push({ kind: 'operator', text: op, op, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }
    if (c === '%') {
      i++
      tokens.push({ kind: 'percent', text: c, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }
    if (c === ':') {
      i++
      tokens.push({ kind: 'colon', text: c, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }
    if (c === '(') {
      i++
      tokens.push({ kind: 'open', text: c, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }
    if (c === ')') {
      i++
      tokens.push({ kind: 'close', text: c, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }
    if (c === locale.argument) {
      i++
      tokens.push({ kind: 'argSep', text: c, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }

    // A word is either a function call or an unquoted container name — quoting is optional when
    // the name needs no escaping. `.` is NOT part of a word any more (it separates containers),
    // so the dotted-function message is rebuilt with an explicit lookahead instead.
    if (NAME_START.test(c)) {
      while (i < source.length) {
        const next = codePointAt(source, i)
        if (!NAME_PART.test(next)) break
        i += next.length
      }
      const word = source.slice(start, i)

      // `SUMA.SI(` is one function attempt, not `SUMA` `.` `SI(`. Only a trailing `(` makes it
      // one, which is exactly what keeps `Ventas.Hoja1[A]` a two-container path.
      let probe = i
      while (source.charAt(probe) === '.') {
        let after = probe + 1
        while (after < source.length) {
          const next = codePointAt(source, after)
          if (!NAME_PART.test(next)) break
          after += next.length
        }
        if (after === probe + 1) break
        probe = after
      }
      if (source.charAt(probe) === '(') {
        const dotted = source.slice(start, probe)
        if (!(dotted.toLocaleUpperCase('es') in FUNCTIONS)) fail(`La función «${dotted}» no existe.`, start, probe)
        i = probe
        tokens.push({ kind: 'func', text: dotted, name: dotted.toLocaleUpperCase('es'), start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
        continue
      }

      const name = word.toLocaleUpperCase('es')
      if (name in FUNCTIONS && source.charAt(i) === '(') {
        tokens.push({ kind: 'func', text: word, name, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
        continue
      }
      tokens.push({ kind: 'containerName', text: word, name: word, quoted: false, start, end: i, ...(tokenStartNl ? { nl: true } : {}) })
      continue
    }

    i += c.length
    fail(`Carácter no admitido: «${c}».`, start, i)
  }
  tokens.push({ kind: 'end', text: '', start: source.length, end: source.length, ...(hadNewline ? { nl: true } : {}) })
  return tokens
}

// ── Parser ────────────────────────────────────────────────────────────────────────────────────

/** How the contents of `[...]` are read: as a visible name to resolve, or as a stored field. */
type RefMode = { by: 'name'; scope: CellFormulaScope } | { by: 'field' }

function parseWith(
  source: string,
  mode: RefMode,
  locale: FormulaLocale,
): { ast: AstNode; refs: string[]; warnings: FormulaWarning[] } {
  if (source.trim() === '') fail('Escribí una fórmula.', 0, source.length)

  const tokens = tokenize(source, locale)
  const refs: string[] = []
  const warnings: FormulaWarning[] = []
  let pos = 0
  // `tokenize` always appends an `end` token, so `pos` is never past the array
  // in practice. The fallback exists to say that in the type system rather than
  // with an assertion, and it is the same token the stream ends with.
  const END: Token = { kind: 'end', text: '', start: source.length, end: source.length }
  const peek = (): Token => tokens[pos] ?? END
  /** Names bound by the enclosing LETs, innermost last, upper-cased. */
  const letNames: string[] = []
  const isLetName = (token: Token): boolean =>
    token.kind === 'containerName' && token.quoted !== true && token.name !== undefined &&
    letNames.includes(token.name.toLocaleUpperCase('es'))

  /** Precedence climbing. `minBp` is the lowest binding power this call may absorb. */
  const parseExpression = (minBp: number): AstNode => {
    let left = parseOperand()

    for (;;) {
      const token = peek()

      if (token.kind === 'colon') {
        if (left.kind !== 'ref') fail('El inicio de un rango debe ser una referencia.', token.start, token.end)
        const leftRef = left as Extract<AstNode, { kind: 'ref' }>
        pos++
        let rightRef: Extract<AstNode, { kind: 'ref' }>
        const endpoint = peek()
        if (leftRef.sheetId !== undefined && endpoint.kind === 'ref' && endpoint.name !== undefined) {
          pos++
          if (endpoint.currentRow || endpoint.row !== undefined) {
            fail('Las referencias directas a celdas de otra hoja no están admitidas.', endpoint.start, endpoint.end)
          }
          const columns = mode.by === 'name' && leftRef.documentId === undefined
            ? mode.scope.columnsOfSheet?.(leftRef.sheetId)
            : undefined
          const field = leftRef.documentId === undefined
            ? resolveRef(endpoint, endpoint.name, columns)
            : endpoint.name
          if (leftRef.documentId === undefined) refs.push(field)
          rightRef = {
            kind: 'ref', field, row: 'column', sheetId: leftRef.sheetId,
            ...(leftRef.documentId === undefined ? {} : { documentId: leftRef.documentId }),
            start: endpoint.start, end: endpoint.end,
          }
        } else {
          const right = parseOperand()
          if (right.kind !== 'ref') fail('El final de un rango debe ser una referencia.', token.start, right.end)
          rightRef = right as Extract<AstNode, { kind: 'ref' }>
        }
        if ((leftRef.sheetId ?? null) !== (rightRef.sheetId ?? null)
          || (leftRef.documentId ?? null) !== (rightRef.documentId ?? null)) {
          fail('Un rango no puede abarcar hojas distintas.', leftRef.start, rightRef.end)
        }
        left = { kind: 'range', from: leftRef, to: rightRef, start: leftRef.start, end: rightRef.end }
        continue
      }

      if (token.kind === 'percent') {
        if (BP_PERCENT < minBp) break
        pos++
        left = { kind: 'percent', arg: left, start: left.start, end: token.end }
        continue
      }

      if (token.kind !== 'operator' || token.op === undefined) break
      const op = token.op
      const [leftBp, rightBp] = BP[op]
      if (leftBp < minBp) break
      const opToken = token
      pos++
      const right = parseExpression(rightBp)
      if (opToken.nl) right.nl = true
      left = { kind: 'binary', op, left, right, start: left.start, end: right.end }
    }

    return left
  }

  /**
   * An operand: a prefix `+`/`-`, a number, a reference, or a parenthesised expression. It
   * takes no `minBp` on purpose — a prefix sign is accepted in operand position whatever the
   * surrounding binding power, and that is exactly what makes `2^-1` valid.
   */
  const parseOperand = (): AstNode => {
    const token = peek()
    const operandNl = token.nl
    let node: AstNode

    if (token.kind === 'operator' && (token.op === '+' || token.op === '-')) {
      pos++
      const arg = parseExpression(BP_UNARY)
      node = { kind: 'unary', op: token.op, arg, start: token.start, end: arg.end }
    } else if (token.kind === 'number' && token.n !== undefined) {
      pos++
      node = { kind: 'number', n: token.n, text: token.text, start: token.start, end: token.end }
    } else if (token.kind === 'string' && token.name !== undefined) {
      pos++
      node = { kind: 'text', s: token.name, start: token.start, end: token.end }
    } else if (token.kind === 'func' && token.name !== undefined) {
      pos++
      node = parseCall(token, token.name)
    } else if (token.kind === 'ref' && token.name !== undefined) {
      pos++
      node = parseReference(token)
    } else if (isLetName(token) && tokens[pos + 1]?.kind !== 'dot' && tokens[pos + 1]?.kind !== 'ref') {
      pos++
      node = { kind: 'name', name: token.name!, start: token.start, end: token.end }
    } else if (token.kind === 'containerName' && token.name !== undefined) {
      node = parseContainerPath()
    } else if (token.kind === 'open') {
      pos++
      const arg = parseExpression(0)
      const close = peek()
      if (close.kind !== 'close') fail('Falta cerrar el paréntesis.', token.start, close.end)
      pos++
      node = { kind: 'group', arg, start: token.start, end: close.end }
    } else {
      if (token.kind === 'operator') fail(`Sobra el operador «${token.text}».`, token.start, token.end)
      if (token.kind === 'close') fail('Sobra el paréntesis de cierre.', token.start, token.end)
      if (token.kind === 'argSep') fail(`Sobra el separador «${token.text}».`, token.start, token.end)
      if (token.kind === 'colon') fail('Sobra el separador de rango «:».', token.start, token.end)
      if (token.kind === 'percent') fail('Se esperaba un número o una columna.', token.start, token.end)
      return fail('Se esperaba un número o una columna.', token.start, token.end)
    }
    if (operandNl) node.nl = true
    return node
  }

  /** `name(` already consumed as one token; the `(` is still ahead. Arity is checked here so the
   *  error can point at the call and name the function, instead of failing at evaluation time. */
  const parseCall = (token: Token, name: string): AstNode => {
    // The tokenizer already rejected any name outside FUNCTIONS, so this cannot
    // miss. Reading it into a local and failing explicitly states that in the
    // types, and keeps the arity check below off a possibly-undefined value.
    const spec = FUNCTIONS[name];
    if (spec === undefined) return fail(`La función «${token.text}» no existe.`, token.start, token.end);
    const open = peek()
    if (open.kind !== 'open') return fail(`Falta abrir el paréntesis de «${token.text}».`, token.start, open.end)
    pos++
    const args: AstNode[] = []
    if (name === 'LET') return parseLet(token, args)
    if (peek().kind !== 'close') {
      for (;;) {
        const argNl = peek().nl
        const expr = parseExpression(0)
        if (argNl) expr.nl = true
        args.push(expr)
        if (peek().kind !== 'argSep') break
        pos++
      }
    }
    const close = peek()
    if (close.kind !== 'close') fail(`Falta cerrar el paréntesis de «${token.text}».`, token.start, close.end)
    pos++
    const validArity =
      typeof spec.arity === 'number'
        ? args.length === spec.arity
        : args.length >= spec.arity.min && (spec.arity.max === undefined || args.length <= spec.arity.max)
    if (!validArity) {
      const expected =
        typeof spec.arity === 'number'
          ? `${spec.arity}`
          : spec.arity.max !== undefined
            ? `entre ${spec.arity.min} y ${spec.arity.max}`
            : `al menos ${spec.arity.min}`
      fail(`«${token.text}» lleva ${expected} argumento(s), no ${args.length}.`, token.start, close.end)
    }
    return { kind: 'call', name, args, start: token.start, end: close.end }
  }

  /** `LET(` with the `(` consumed. Even arguments before the last are bare names, each visible to
   *  every argument after it; they are kept in `args` as `name` nodes so serializing needs nothing new. */
  const parseLet = (token: Token, args: AstNode[]): AstNode => {
    const depth = letNames.length
    for (;;) {
      const argNl = peek().nl
      const next = peek()
      const after = tokens[pos + 1]
      if (next.kind === 'containerName' && next.quoted !== true && next.name !== undefined && after?.kind === 'argSep') {
        // A name position: a bare word followed by a separator.
        const upper = next.name.toLocaleUpperCase('es')
        if (upper in FUNCTIONS) fail(`«${next.text}» es una función; elegí otro nombre.`, next.start, next.end)
        if (letNames.slice(depth).includes(upper)) fail(`El nombre «${next.text}» ya está definido en este LET.`, next.start, next.end)
        pos += 2
        const nameNode: AstNode = { kind: 'name', name: next.name, start: next.start, end: next.end }
        if (argNl) nameNode.nl = true
        args.push(nameNode)
        const valueNl = peek().nl
        const value = parseExpression(0)
        if (valueNl) value.nl = true
        args.push(value)
        letNames.push(upper)
        if (peek().kind !== 'argSep') {
          letNames.length = depth
          return fail('Después del último valor de LET falta la expresión.', token.start, peek().end)
        }
        pos++
        continue
      }
      if (args.length === 0) {
        letNames.length = depth
        return fail('LET empieza con un nombre, por ejemplo LET(x; 1; x+1).', next.start, next.end)
      }
      const body = parseExpression(0)
      if (argNl) body.nl = true
      args.push(body)
      letNames.length = depth
      const close = peek()
      if (close.kind === 'argSep') fail('Cada nombre de LET necesita un valor; el último argumento es la expresión.', close.start, close.end)
      if (close.kind !== 'close') fail(`Falta cerrar el paréntesis de «${token.text}».`, token.start, close.end)
      pos++
      return { kind: 'call', name: 'LET', args, start: token.start, end: close.end }
    }
  }

  /**
   * `'Doc'.'Sheet'[Col]`, `Sheet[Col]`, and everything between. Reads up to two container names
   * separated by dots, then requires the bracket.
   *
   * The stray dot the brief calls out — `'Sheet'.[Col]` — is swallowed HERE with a warning rather
   * than rejected, and only when it sits directly before `[`. A dot between two names is a real
   * separator and never reaches this branch.
   */
  const parseContainerPath = (): AstNode => {
    const path: Token[] = []
    for (;;) {
      const name = peek()
      if (name.kind !== 'containerName' || name.name === undefined) {
        return fail('Se esperaba el nombre de una hoja o un documento.', name.start, name.end)
      }
      if (path.length === 2) {
        return fail('Una referencia admite como mucho un documento y una hoja.', path[0]!.start, name.end)
      }
      path.push(name)
      pos++

      if (peek().kind !== 'dot') break
      const dot = peek()
      pos++
      // `'Sheet'.[Col]`: accepted, the dot is dropped, and serializing writes the fix back.
      if (peek().kind === 'ref') {
        const canonical = `${path.map(containerText).join('.')}${peek().text}`
        warnings.push({
          message: `Sobra el punto antes de «[». Se escribe «${canonical}».`,
          code: 'stray-dot-before-bracket',
          data: { canonical },
          start: dot.start,
          end: dot.end,
        })
        break
      }
    }

    const ref = peek()
    if (ref.kind !== 'ref' || ref.name === undefined) {
      // A single BARE word with no bracket after it is almost always a function call attempt
      // rather than a sheet — `CHAR 10`, `SUMA(...)`. Those messages are much more useful than
      // one about a missing bracket, so they are kept for exactly that shape.
      const only = path.length === 1 ? path[0]! : undefined
      if (only !== undefined && only.quoted !== true) {
        if (isGlobalFormatName(only.name ?? '')) {
          return { kind: 'text', s: only.name ?? '', start: only.start, end: only.end }
        }
        if ((only.name ?? '').toLocaleUpperCase('es') in FUNCTIONS) {
          return fail(`Falta abrir el paréntesis de «${only.text}».`, only.start, only.end)
        }
        return fail(`La función «${only.text}» no existe.`, only.start, only.end)
      }
      return fail('Después del nombre de la hoja debe haber una columna entre corchetes.', path[0]!.start, ref.end)
    }
    pos++
    return parseReference(ref, path)
  }

  /** A path token rendered back the way it was written, for use inside a diagnostic. */
  const containerText = (token: Token): string => token.text

  const parseReference = (token: Token, path: readonly Token[] = []): Extract<AstNode, { kind: 'ref' }> => {
    const documentToken = path.length === 2 ? path[0] : undefined
    const sheetToken = path.length === 2 ? path[1] : path[0]
    const document = resolveDocument(documentToken)
    const sheet = resolveSheet(sheetToken, document)
    // A column inside another document cannot be checked against anything loaded, so its NAME is
    // kept as the field. That is the one place the id-only rule bends, and it bends knowingly:
    // evaluation returns `#¡REF!` for these until cross-document loading exists.
    const field =
      document === undefined
        ? resolveRef(token, token.name ?? '', sheet?.columns)
        : (token.name ?? '')
    let row: FormulaRowReference = token.currentRow ? 'current' : 'column'
    if (token.row !== undefined) {
      // `'Hoja'[@Col]` IS allowed — same row, other sheet — but pinning a specific row of another
      // sheet is not: the row numbering of two sheets has nothing to do with each other.
      if (sheet !== undefined) fail('Las referencias directas a celdas de otra hoja no están admitidas.', sheetToken?.start ?? token.start, token.end)
      if (typeof token.row === 'number') {
        if (mode.by === 'field') row = { id: String(token.row) }
        else {
          const id = mode.scope.rowIdAt?.(token.row)
          if (id === undefined) fail(`La fila ${token.row} no existe.`, token.start, token.end)
          row = { id: id as string }
        }
      } else row = { id: token.row }
    }
    if (sheet !== undefined && typeof row === 'object') {
      fail('Las referencias directas a celdas de otra hoja no están admitidas.', sheetToken?.start ?? token.start, token.end)
    }
    // Only a same-document reference contributes to the dependency graph; a cross-document one
    // has nothing in this workbook to depend on.
    if (document === undefined) refs.push(field)
    return {
      kind: 'ref', field, row, start: (documentToken ?? sheetToken)?.start ?? token.start, end: token.end,
      ...(sheet === undefined ? {} : { sheetId: sheet.id }),
      ...(document === undefined ? {} : { documentId: document.id }),
    }
  }

  const resolveDocument = (token?: Token): { id: string; name: string } | undefined => {
    if (token?.name === undefined) return undefined
    if (mode.by === 'field') return { id: token.name, name: token.name }
    const found = mode.scope.documentOfName?.(token.name)
    if (found === 'ambiguous') return fail(`El nombre «${token.name}» corresponde a más de un documento.`, token.start, token.end)
    // No cross-document scope loaded: accept the name as an opaque id rather than reject a
    // formula the grammar allows. The runtime is what reports it, as `#¡REF!`.
    if (found === undefined) return { id: token.name, name: token.name }
    return found
  }

  const resolveSheet = (
    token: Token | undefined,
    document: { id: string; name: string } | undefined,
  ): FormulaSheetScope | undefined => {
    if (token?.name === undefined) return undefined
    if (mode.by === 'field' || document !== undefined) {
      // Inside another document there is nothing to look the sheet up in either.
      return { id: token.name, name: token.name, columns: EMPTY_COLUMN_SCOPE }
    }
    const found = mode.scope.sheetOfName?.(token.name)
    if (found === 'ambiguous') return fail(`El nombre «${token.name}» corresponde a más de una hoja.`, token.start, token.end)
    if (found === undefined) return fail(`La hoja «${token.name}» no existe.`, token.start, token.end)
    return found
  }

  const resolveRef = (token: Token, name: string, sheetColumns?: ColumnScope): string => {
    if (mode.by === 'field') return name
    const found = (sheetColumns ?? mode.scope).fieldOfName(name)
    if (found.ok) return found.field
    if (found.reason === 'ambiguous') {
      return fail(`El nombre «${name}» corresponde a más de una columna.`, token.start, token.end)
    }
    return fail(`La columna «${name}» no existe en esta planilla.`, token.start, token.end)
  }

  const ast = parseExpression(0)
  const trailing = peek()
  if (trailing.kind !== 'end') {
    if (trailing.kind === 'close') fail('Sobra el paréntesis de cierre.', trailing.start, trailing.end)
    // An argument separator outside a call: `1;2` is two expressions, not one formula.
    if (trailing.kind === 'argSep') fail(`Sobra el separador «${trailing.text}».`, trailing.start, trailing.end)
    fail(`Sobra «${trailing.text}».`, trailing.start, trailing.end)
  }
  return { ast, refs, warnings }
}

function run(body: () => { ast: AstNode; refs: string[]; warnings: FormulaWarning[] }): ParseResult {
  try {
    const { ast, refs, warnings } = body()
    return { ok: true, ast, refs: [...new Set(refs)], references: formulaReferences(ast), warnings }
  } catch (e) {
    if (e instanceof ParseFailure) return { ok: false, error: e.detail }
    throw e
  }
}

/** Parses what the user typed: visible column names, the locale's decimal separator. A
 *  reference to a missing column IS a syntax error here, so the editor can point at it. */
export function parseFormula(source: string, scope: CellFormulaScope, locale: FormulaLocale): ParseResult {
  return run(() => parseWith(source, { by: 'name', scope }, locale))
}

export const parseCellFormula = parseFormula

/** Parses the stored canonical text: fields in brackets, `.` decimal. A reference to a missing
 *  column is NOT a syntax error here — it evaluates to `#¡REF!`, so a column removed from the
 *  code degrades to an error cell instead of making the whole formula disappear. */
export function parseCanonical(source: string): ParseResult {
  return run(() => parseWith(source, { by: 'field' }, CANONICAL_LOCALE))
}

// ── Serializer ────────────────────────────────────────────────────────────────────────────────

const escapeRefName = (name: string): string => name.replace(/]/g, ']]')
const escapeTextLiteral = (s: string): string => s.replace(/"/g, '""')

const escapeApostrophe = (text: string): string => text.replace(/'/g, "''")

/**
 * Whether a container name can be written bare. Quotes are optional, so the serializer has to
 * decide, and the completion source has to make the SAME decision or the editor would insert
 * something that round-trips differently from what a save produces. Both call this.
 *
 * Conservative on purpose: anything that is not a letter, digit or underscore gets quoted, and so
 * does a name starting with a digit (which would begin a number) or one that is spelled like a
 * function (which would be read as a call).
 */
export function containerNameNeedsQuotes(name: string): boolean {
  if (name === '') return true
  // Defined from the lexer's own classes rather than restating them, so a name written bare is
  // always a name that reads back bare. Emoji qualify; a leading digit and a space never do.
  const first = codePointAt(name, 0)
  if (!NAME_START.test(first)) return true
  for (let i = first.length; i < name.length;) {
    const next = codePointAt(name, i)
    if (!NAME_PART.test(next)) return true
    i += next.length
  }
  return name.toLocaleUpperCase('es') in FUNCTIONS
}

/** A container name written the canonical way: bare when it can be, quoted when it must be. */
export function quoteContainerName(name: string): string {
  return containerNameNeedsQuotes(name) ? `'${escapeApostrophe(name)}'` : name
}

/** `'Doc'.'Sheet'` — and never a dot before the bracket the caller appends. */
function containerPrefix(parts: readonly string[]): string {
  return parts.length === 0 ? '' : `${parts.map(quoteContainerName).join('.')}`
}

function serializeNode(
  node: AstNode,
  locale: FormulaLocale,
  refText: (node: Extract<AstNode, { kind: 'ref' }>) => string,
): string {
  const child = (n: AstNode): string => serializeNode(n, locale, refText)
  switch (node.kind) {
    case 'text':
      return `"${escapeTextLiteral(node.s)}"`
    case 'call': {
      const anyArgHasNl = node.args.some((a) => a.nl)
      const argsText = node.args
        .map((a, i) => (a.nl ? '\n  ' : (i > 0 && anyArgHasNl ? ' ' : '')) + child(a))
        .join(locale.argument)
      return `${node.name}(${argsText})`
    }
    case 'number':
      // Reuse the original text so `0.1+0.2` never becomes `0.30000000000000004`.
      return locale.decimal === CANONICAL_LOCALE.decimal
        ? node.text
        : node.text.replace(CANONICAL_LOCALE.decimal, locale.decimal)
    case 'ref':
      return refText(node)
    case 'range':
      return child(node.from) + ':' + child(node.to)
    case 'unary':
      return node.op + child(node.arg)
    case 'binary': {
      const glue = node.right.nl ? ` ${node.op}\n  ` : node.op
      return child(node.left) + glue + child(node.right)
    }
    case 'percent':
      return child(node.arg) + '%'
    case 'group':
      return '(' + child(node.arg) + ')'
    case 'name':
      return node.name
    case 'raw':
      // Verbatim, in whatever locale it was written: it never parsed, so there is nothing to
      // translate and guessing would corrupt what the user is trying to repair.
      return node.text
  }
}

/** AST → the text stored in `ajustes`: fields in brackets, `.` decimal. */
export function serializeCanonical(ast: AstNode): string {
  // The number nodes already hold canonical text, since that is what the tokenizer read.
  return serializeNode(ast, CANONICAL_LOCALE, (ref) => {
    // Ids, always quoted: a uuid contains hyphens, so it could never be written bare anyway, and
    // quoting unconditionally keeps the stored text stable if the id format ever changes.
    const parts = [
      ...(ref.documentId === undefined ? [] : [`'${escapeApostrophe(ref.documentId)}'`]),
      ...(ref.sheetId === undefined ? [] : [`'${escapeApostrophe(ref.sheetId)}'`]),
    ]
    const at = ref.row === 'current' ? '@' : ''
    const row = typeof ref.row === 'object' ? `@'${escapeApostrophe(ref.row.id)}'` : ''
    return `${parts.join('.')}[${at}${escapeRefName(ref.field)}]${row}`
  })
}

/** AST → text to show and edit: current visible headers, the user's decimal separator. A
 *  reference whose column no longer exists renders as `[#¡REF!]` — visible, and deliberately
 *  not parseable, so it cannot be saved back as if it were a real column. */
export function serialize(ast: AstNode, scope: CellFormulaScope, locale: FormulaLocale): string {
  return serializeNode(ast, locale, (ref) => {
    // A cross-document reference keeps names, not ids, because nothing about the other document
    // is loaded to translate them with — see `parseReference`. Rendering them back verbatim is
    // what lets such a formula survive being opened and saved.
    const external = ref.documentId !== undefined
    const columnScope = ref.sheetId === undefined ? scope : scope.columnsOfSheet?.(ref.sheetId)
    const name = external ? ref.field : (columnScope?.nameOfField(ref.field) ?? '#¡REF!')
    const sheetName = external
      ? ref.sheetId
      : ref.sheetId === undefined ? undefined : (scope.sheetNameOfId?.(ref.sheetId) ?? '#¡REF!')
    const documentName = external
      ? (scope.documentNameOfId?.(ref.documentId as string) ?? ref.documentId as string)
      : undefined
    const parts = [
      ...(documentName === undefined ? [] : [documentName]),
      ...(sheetName === undefined ? [] : [sheetName]),
    ]
    const at = ref.row === 'current' ? '@' : ''
    const row = typeof ref.row === 'object' ? scope.rowIndexOf?.(ref.row.id) : undefined
    const suffix = typeof ref.row === 'object' ? (row === undefined ? '#¡REF!' : String(row)) : ''
    return `${containerPrefix(parts)}[${at}${escapeRefName(name)}]${suffix}`
  })
}

export const serializeCellFormula = serialize

/**
 * Whether the formula can produce a TEXT value: it has a literal, a `&`, or a function call.
 * Purely syntactic — a formula over a text COLUMN (`[Descripción]` alone) is not included,
 * because the column already carries its own type and alignment. The Grid uses this to align a
 * calculated column left and to honour the `CHAR(10)` line breaks inside it.
 */
export function producesText(node: AstNode): boolean {
  switch (node.kind) {
    case 'text':
    case 'call':
      return true
    case 'number':
    case 'ref':
    case 'range':
    case 'name':
      return false
    // An error code is text as far as alignment goes, and `raw` always evaluates to one.
    case 'raw':
      return true
    case 'unary':
    case 'percent':
    case 'group':
      return producesText(node.arg)
    case 'binary':
      return node.op === '&' || producesText(node.left) || producesText(node.right)
  }
}

// ── Cell reading ──────────────────────────────────────────────────────────────────────────────

/** Digits with optional thousands separators and one decimal separator, either convention. */
const NUMERIC_TEXT = /^[+-]?(\d+([.,]\d{3})*|\d*)([.,]\d+)?$/

/** The number a string denotes, or `null` when it is ordinary text. Shared by cell reading and
 *  by the arithmetic coercion of a `text` value, so both accept exactly the same spellings. */
function numericText(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '' || !NUMERIC_TEXT.test(trimmed)) return null
  // Whichever separator appears last is the decimal one; the other groups thousands.
  const lastDot = trimmed.lastIndexOf('.')
  const lastComma = trimmed.lastIndexOf(',')
  let plain: string
  if (lastDot === -1 && lastComma === -1) plain = trimmed
  else if (lastComma > lastDot) plain = trimmed.replace(/\./g, '').replace(',', '.')
  else plain = trimmed.replace(/,/g, '')
  const n = Number(plain)
  return Number.isFinite(n) ? n : null
}

/**
 * Converts what `readValue` gave us into a formula value.
 * Numbers, numeric strings, Dates, and date strings (ISO, dd/mm/yyyy, dd/mm)
 * are coerced to numbers (Dates to Excel day serial numbers).
 */
export function fromCell(value: unknown): FormulaValue {
  if (value === null || value === undefined) return EMPTY
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? error('#¡VALOR!') : number(dateToSerial(value))
  }
  if (typeof value === 'number') return Number.isFinite(value) ? number(value) : error('#¡VALOR!')
  if (typeof value === 'boolean') return number(value ? 1 : 0)
  if (typeof value === 'string') {
    if (value.trim() === '') return EMPTY
    const n = numericText(value)
    if (n !== null) return number(n)
    const d = parseGridDate(value)
    if (d !== null) return number(dateToSerial(d))
    // Text that is neither a number nor a date is a TEXT value, not an error: `&` concatenates it,
    // and arithmetic operators report `#¡VALOR!` for it (see `arithmetic`).
    return text(value)
  }
  return error('#¡VALOR!')
}

// ── Evaluator ─────────────────────────────────────────────────────────────────────────────────

export type RefResolver = (field: string) => FormulaValue

export type FormulaResolveIntent = 'scalar' | 'collection'

export interface CellFormulaEvaluationContext {
  resolveReference: (reference: FormulaReference, intent: FormulaResolveIntent) => FormulaValue
  resolveRange: (from: FormulaReference, to: FormulaReference) => FormulaValue
  /** 1-based row number of a reference (`row: 'current'` is the formula's own row). Optional:
   *  without it ROW() is `#¡REF!`. */
  rowNumberOf?: (reference: FormulaReference) => number | undefined
}

/**
 * Coerces `empty` to 0, propagates the LEFTMOST error, and only then applies `f`. Every
 * arithmetic operator goes through here and none of them reads the union directly.
 *
 * This is the extension point for future functions: each one declares its own empty policy
 * (`'zero' | 'ignore' | 'propagate'`), and the ones that propagate receive the RAW values
 * without calling this. Empty-handling is a property of the operation, not of the evaluator.
 */
// The tuple generic is Numtable's addition to the port: under
// `noUncheckedIndexedAccess`, a `number[]` callback parameter makes every
// destructured `a`/`b` possibly-undefined, and the alternative was a non-null
// assertion on each arm. Threading the arity through the type instead keeps the
// bodies below identical to TMS's and still provably total.
/**
 * Excel's comparison: an empty side takes the other side's type (0 or ""), text compares
 * case-insensitively, and across types every number is less than every text. Errors propagate.
 * The result is 1 or 0, the stand-in for TRUE/FALSE in a grammar without booleans.
 */
function compare(op: ComparisonOperator, left: FormulaValue, right: FormulaValue): FormulaValue {
  if (left.kind === 'error') return left
  if (right.kind === 'error') return right
  if (left.kind === 'collection' || right.kind === 'collection') return error('#¡VALOR!')
  const side = (v: FormulaValue, other: FormulaValue): number | string =>
    v.kind === 'number' ? v.n : v.kind === 'text' ? v.s : other.kind === 'text' ? '' : 0
  const a = side(left, right)
  const b = side(right, left)
  let cmp: number
  if (typeof a === 'number' && typeof b === 'number') cmp = Math.sign(a - b)
  else if (typeof a === 'string' && typeof b === 'string') cmp = Math.sign(a.localeCompare(b, 'es', { sensitivity: 'base' }))
  else cmp = typeof a === 'number' ? -1 : 1
  const result = op === '=' ? cmp === 0
    : op === '<>' ? cmp !== 0
    : op === '<' ? cmp < 0
    : op === '>' ? cmp > 0
    : op === '<=' ? cmp <= 0
    : cmp >= 0
  return number(result ? 1 : 0)
}

/** A condition as Excel reads it: non-zero numbers are true, empty is false, and text counts only
 *  when it spells TRUE/FALSE (or VERDADERO/FALSO). Anything else is the error to return. */
/** AND / OR over scalars and ranges, with Excel's coercion: errors win, range text/blanks skip. */
function logicalFold(args: readonly FormulaValue[], mode: 'and' | 'or'): FormulaValue {
  let seen = false
  let result = mode === 'and'
  const visit = (value: FormulaValue, fromCollection: boolean): FormulaValue | null => {
    switch (value.kind) {
      case 'error':
        return value
      case 'collection':
        for (const member of value.values) {
          const failed = visit(member, true)
          if (failed !== null) return failed
        }
        return null
      case 'empty':
        return null
      case 'text': {
        if (fromCollection) return null
        const test = truthiness(value)
        if (typeof test !== 'boolean') return test
        seen = true
        result = mode === 'and' ? result && test : result || test
        return null
      }
      case 'number':
        seen = true
        result = mode === 'and' ? result && value.n !== 0 : result || value.n !== 0
        return null
    }
  }
  for (const arg of args) {
    const failed = visit(arg, false)
    if (failed !== null) return failed
  }
  return seen ? number(result ? 1 : 0) : error('#¡VALOR!')
}

/** ROUND / ROUNDUP / ROUNDDOWN. `toPrecision(15)` absorbs binary noise so ROUND(2,675;2) is 2,68. */
function roundTo(n: number, digits: number, mode: 'half' | 'up' | 'down'): FormulaValue {
  const d = Math.trunc(digits)
  const factor = 10 ** Math.abs(d)
  const abs = Math.abs(n)
  const scaled = Number((d >= 0 ? abs * factor : abs / factor).toPrecision(15))
  const rounded = mode === 'half' ? Math.round(scaled) : mode === 'up' ? Math.ceil(scaled) : Math.floor(scaled)
  const back = d >= 0 ? rounded / factor : rounded * factor
  const result = Math.sign(n) * back
  return Number.isFinite(result) ? number(result || 0) : error('#¡NUM!')
}

/** A part of a date for DAY / MONTH / YEAR; negative serials are `#¡NUM!`. */
function datePart(value: FormulaValue, part: (d: Date) => number): FormulaValue {
  return arithmetic([value], ([serial]) =>
    serial < 0 ? error('#¡NUM!') : number(part(serialToDate(Math.floor(serial)))),
  )
}

function truthiness(value: FormulaValue): boolean | FormulaValue {
  switch (value.kind) {
    case 'error':
      return value
    case 'empty':
      return false
    case 'number':
      return value.n !== 0
    case 'text': {
      const word = value.s.trim().toLocaleUpperCase('es')
      if (word === 'TRUE' || word === 'VERDADERO') return true
      if (word === 'FALSE' || word === 'FALSO') return false
      return error('#¡VALOR!')
    }
    case 'collection':
      return error('#¡VALOR!')
  }
}

function arithmetic<const N extends readonly FormulaValue[]>(
  values: N,
  f: (ns: { -readonly [K in keyof N]: number }) => FormulaValue,
): FormulaValue {
  const ns: number[] = []
  for (const value of values) {
    switch (value.kind) {
      case 'error':
        return value
      case 'empty':
        ns.push(0)
        break
      case 'number':
        ns.push(value.n)
        break
      case 'text': {
        // As in Excel: text that spells a number or a date takes part in arithmetic, anything else is
        // `#¡VALOR!`. Same spellings a cell accepts, so `[A]+1` does not depend on whether the
        // column arrived as a string.
        const n = numericText(value.s)
        if (n !== null) {
          ns.push(n)
          break
        }
        const d = parseGridDate(value.s)
        if (d !== null) {
          ns.push(dateToSerial(d))
          break
        }
        return error('#¡VALOR!')
      }
      case 'collection':
        return error('#¡VALOR!')
    }
  }
  return f(ns as { -readonly [K in keyof N]: number })
}

/**
 * `&` coercion: the mirror image of `arithmetic`. `empty` becomes `""` (Excel's own rule, which
 * is why `[Vacía]&"x"` is `"x"` and not `"0x"`), a number becomes its plain digits, and the
 * leftmost error still wins.
 *
 * A number is written with a DOT decimal and no thousands separators, which is what `String(n)`
 * gives. Excel would use the locale's separator here; matching that needs the user's locale
 * inside the evaluator, which it deliberately does not have (see docs/10, "Text and `&`").
 */
function concat(values: readonly FormulaValue[]): FormulaValue {
  let out = ''
  for (const value of values) {
    switch (value.kind) {
      case 'error':
        return value
      case 'empty':
        break
      case 'number':
        out += String(value.n)
        break
      case 'text':
        out += value.s
        break
      case 'collection':
        return error('#¡VALOR!')
    }
  }
  return text(out)
}

/** Guards every arithmetic result: `Infinity` and `NaN` must never reach a cell. */
const finite = (n: number): FormulaValue => (Number.isFinite(n) ? number(n === 0 ? 0 : n) : error('#¡NUM!'))

const referenceOf = (node: Extract<AstNode, { kind: 'ref' }>): FormulaReference => ({
  field: node.field,
  row: node.row ?? 'current',
  ...(node.sheetId === undefined ? {} : { sheetId: node.sheetId }),
})

/** LET bindings in scope, keyed by upper-cased name. Each is evaluated at most once per intent, on
 *  first use, so an unused binding costs nothing and a range binding still works inside SUM. */
type LetEnv = ReadonlyMap<string, (intent: FormulaResolveIntent, broadcast: boolean) => FormulaValue>
const NO_LET: LetEnv = new Map()

/**
 * Which arguments are collections. A whole-column reference only resolves to its values in this
 * intent; everywhere else it is an implicit intersection. Criteria arguments stay scalar.
 */
function wantsCollectionArg(name: string, index: number): boolean {
  switch (name) {
    case 'SUM':
    case 'COUNT':
    case 'COUNTA':
    case 'AVERAGE':
    case 'MAX':
    case 'MIN':
      return true
    case 'COUNTIF':
      return index === 0
    case 'COUNTIFS':
      return index % 2 === 0
    case 'SUMIF':
    case 'AVERAGEIF':
    case 'MAXIF':
    case 'MINIF':
      return index === 0 || index === 2
    case 'SUMIFS':
    case 'AVERAGEIFS':
    case 'MAXIFS':
    case 'MINIFS':
      return index === 0 || index % 2 === 1
    case 'XLOOKUP':
      return index === 1 || index === 2
    case 'FILTER':
      return true
    case 'TAKE':
    case 'ROWS':
    case 'SLICE':
      return index === 0
    case 'UPTO':
      return true
    default:
      return false
  }
}

function evaluateWithContext(
  ast: AstNode,
  context: CellFormulaEvaluationContext,
  intent: FormulaResolveIntent,
  env: LetEnv = NO_LET,
  /** Inside a FILTER condition: operators apply element-wise to whole columns and ranges. */
  broadcast = false,
): FormulaValue {
  switch (ast.kind) {
    case 'name':
      return env.get(ast.name.toLocaleUpperCase('es'))?.(intent, broadcast) ?? error('#¡NOMBRE?')

    case 'number':
      return number(ast.n)

    case 'text':
      return text(ast.s)

    case 'call': {
      if (ast.name === 'LET') {
        const scope = new Map(env)
        for (let i = 0; i + 1 < ast.args.length; i += 2) {
          const nameNode = ast.args[i]!
          const valueNode = ast.args[i + 1]!
          if (nameNode.kind !== 'name') return error('#¡VALOR!')
          const bound: LetEnv = new Map(scope)
          // Keyed by intent and broadcast: a binding used as a FILTER condition (`c = [n]>1`) is
          // evaluated element-wise there and as a scalar everywhere else.
          const cache = new Map<string, FormulaValue>()
          scope.set(nameNode.name.toLocaleUpperCase('es'), (want, wantBroadcast) => {
            const key = `${want}|${wantBroadcast}`
            let value = cache.get(key)
            if (value === undefined) {
              cache.set(key, (value = evaluateWithContext(valueNode, context, want, bound, wantBroadcast)))
            }
            return value
          })
        }
        const body = ast.args[ast.args.length - 1]
        return body === undefined ? error('#¡VALOR!') : evaluateWithContext(body, context, intent, scope, broadcast)
      }
      if (ast.name === 'ROW') return rowNumber(ast.args[0], context)
      // FILTER and TAKE only feed a range argument (SUMIF, SUM, ROWS...); they never spill into
      // the grid, so in a scalar position they are an error.
      const isRange = ast.name === 'FILTER' || ast.name === 'TAKE' || ast.name === 'SLICE' || ast.name === 'UPTO'
      if (isRange && intent !== 'collection') return error('#¡VALOR!')
      if (ast.name === 'COST') return costOf(ast.args, context, intent, env)
      if (ast.name === 'SLICE' || ast.name === 'UPTO') {
        const source = evaluateWithContext(ast.args[0]!, context, 'collection', env)
        const rest = ast.args.slice(1)
        return ast.name === 'SLICE'
          ? sliceRows(source, rest.map((arg) => evaluateWithContext(arg, context, 'scalar', env)), context)
          : uptoRows(source, rest[0] && {
            all: evaluateWithContext(rest[0], context, 'collection', env),
            current: evaluateWithContext(rest[0], context, 'scalar', env),
          }, context)
      }
      const args = ast.args.map((arg, idx) => {
        let argIntent: FormulaResolveIntent = 'scalar'
        if (wantsCollectionArg(ast.name, idx)) {
          argIntent = 'collection'
        }
        return evaluateWithContext(arg, context, argIntent, env, ast.name === 'FILTER' && idx > 0)
      })
      // The name was checked at parse time; a formula stored before a function was removed from
      // the table degrades to #¡NOMBRE?, the same code an unparseable formula shows.
      const spec = FUNCTIONS[ast.name]
      return spec === undefined ? error('#¡NOMBRE?') : spec.call(args)
    }

    case 'ref':
      // The reference itself never coerces: an empty cell yields `empty` and each operator
      // decides. A bare `[A]` is therefore the only formula that can render blank.
      return context.resolveReference(referenceOf(ast), intent)

    case 'raw':
      // Text that never parsed. It is kept so the user can repair it, and it shows the same code
      // an unknown function does — the cell is visibly broken without the formula being lost.
      return error('#¡NOMBRE?')

    case 'range':
      return intent === 'collection'
        ? context.resolveRange(referenceOf(ast.from), referenceOf(ast.to))
        : error('#¡VALOR!')

    case 'group':
      return evaluateWithContext(ast.arg, context, intent, env, broadcast)

    case 'unary': {
      const arg = evaluateWithContext(ast.arg, context, broadcast ? 'collection' : 'scalar', env, broadcast)
      // `n === 0 ? 0 : -n` keeps `-[Empty]` from producing `-0`.
      return elementWise(arg, EMPTY, (a) => arithmetic([a], ([n]) => finite(ast.op === '-' ? (n === 0 ? 0 : -n) : n)))
    }

    case 'percent': {
      const arg = evaluateWithContext(ast.arg, context, broadcast ? 'collection' : 'scalar', env, broadcast)
      return elementWise(arg, EMPTY, (a) => arithmetic([a], ([n]) => finite(n / 100)))
    }

    case 'binary': {
      const operandIntent = broadcast ? 'collection' : 'scalar'
      return elementWise(
        evaluateWithContext(ast.left, context, operandIntent, env, broadcast),
        evaluateWithContext(ast.right, context, operandIntent, env, broadcast),
        (left, right) => binaryOp(ast.op, left, right),
      )
    }
  }
}

function binaryOp(op: BinaryOperator, left: FormulaValue, right: FormulaValue): FormulaValue {
  if (op === '&') return concat([left, right])
  if (isComparison(op)) return compare(op, left, right)
  // Dividing by an empty cell is a row not filled in yet, not a mistake: show it blank
  // rather than #¡DIV/0!. An explicit 0 still is #¡DIV/0!.
  if (op === '/' && right.kind === 'empty' && left.kind !== 'error') return EMPTY
  return arithmetic([left, right], ([a, b]) => {
    switch (op as BinaryOperator) {
      case '+':
        return finite(a + b)
      case '-':
        return finite(a - b)
      case '*':
        return finite(a * b)
      case '/':
        return b === 0 ? error('#¡DIV/0!') : finite(a / b)
      case '^':
        // Even root of a negative is `NaN` in JS; Excel reports #¡NUM!.
        if (a < 0 && !Number.isInteger(b)) return error('#¡NUM!')
        return finite(Math.pow(a, b))
      case '&':
      case '=':
      case '<>':
      case '<':
      case '>':
      case '<=':
      case '>=':
        // Unreachable: `&` and comparisons returned above, before any numeric coercion. The branch exists so
        // that adding an operator to `BinaryOperator` fails to compile until it is handled.
        return error('#¡VALOR!')
    }
  })
}

/** Applies `f` member by member when either side is a collection (a scalar repeats); two
 *  collections must be the same length. Outside FILTER conditions both sides are scalars. */
function elementWise(
  left: FormulaValue,
  right: FormulaValue,
  f: (left: FormulaValue, right: FormulaValue) => FormulaValue,
): FormulaValue {
  if (left.kind !== 'collection' && right.kind !== 'collection') return f(left, right)
  const ls = membersOf(left)
  const rs = membersOf(right)
  if (left.kind === 'collection' && right.kind === 'collection' && ls.length !== rs.length) return error('#¡VALOR!')
  const size = Math.max(ls.length, rs.length)
  const out: FormulaValue[] = []
  for (let i = 0; i < size; i++) out.push(f(ls[ls.length === 1 ? 0 : i] ?? EMPTY, rs[rs.length === 1 ? 0 : i] ?? EMPTY))
  const shaped = left.kind === 'collection' ? left : right
  return collection(out, shaped.kind === 'collection' ? shaped.cols : 1)
}

/** ROW([ref]): the reference's row number, a range's first row, or the formula's own row. */
function rowNumber(arg: AstNode | undefined, context: CellFormulaEvaluationContext): FormulaValue {
  let target: AstNode | undefined = arg
  while (target?.kind === 'group') target = target.arg
  let reference: FormulaReference
  if (target === undefined) reference = { field: '', row: 'current' }
  else if (target.kind === 'ref') reference = referenceOf(target)
  else if (target.kind === 'range') reference = referenceOf(target.from)
  else return error('#¡VALOR!')
  const n = context.rowNumberOf?.(reference)
  return n === undefined ? error('#¡REF!') : number(n)
}

const currentRowOf = (context: CellFormulaEvaluationContext): number | undefined =>
  context.rowNumberOf?.({ field: '', row: 'current' })

/** Keeps the rows `[from, to)` (0-based) of a range, or `#¡VALOR!` when nothing is left. */
function keepRows(source: ReturnType<typeof shapeOf>, keep: (row: number) => boolean): FormulaValue {
  if (source.kind === 'error') return source
  const out: FormulaValue[] = []
  for (let r = 0; r < source.rows; r++) {
    if (!keep(r)) continue
    for (let c = 0; c < source.cols; c++) out.push(source.values[r * source.cols + c] ?? EMPTY)
  }
  return out.length === 0 ? error('#¡VALOR!') : collection(out, source.cols)
}

/** SLICE(range; [from]; [to]): rows `from`..`to`, 1-based and inclusive. */
function sliceRows(source: FormulaValue, bounds: FormulaValue[], context: CellFormulaEvaluationContext): FormulaValue {
  const bound = (value: FormulaValue | undefined, fallback: () => number | undefined): number | FormulaValue => {
    // The grammar has no empty arguments, so 0 or "" also stand for "not given".
    if (value === undefined || value.kind === 'empty' || (value.kind === 'text' && value.s === '')) {
      return fallback() ?? error('#¡REF!')
    }
    const n = arithmetic([value], ([x]) => number(Math.trunc(x)))
    if (n.kind !== 'number') return n
    return n.n === 0 ? (fallback() ?? error('#¡REF!')) : n.n
  }
  const from = bound(bounds[0], () => 1)
  if (typeof from !== 'number') return from
  const to = bound(bounds[1], () => currentRowOf(context))
  if (typeof to !== 'number') return to
  if (from < 1 || to < from) return error('#¡VALOR!')
  return keepRows(shapeOf(source), (r) => r + 1 >= from && r + 1 <= to)
}

/** UPTO(range; [order]): rows up to the current one, or by `order` with ties cut at the current row. */
function uptoRows(
  source: FormulaValue,
  order: { all: FormulaValue; current: FormulaValue } | undefined,
  context: CellFormulaEvaluationContext,
): FormulaValue {
  const current = currentRowOf(context)
  if (current === undefined) return error('#¡REF!')
  const shape = shapeOf(source)
  if (order === undefined) return keepRows(shape, (r) => r + 1 <= current)
  if (order.current.kind === 'error') return order.current
  const keys = membersOf(order.all)
  if (shape.kind !== 'error' && keys.length !== shape.rows) return error('#¡VALOR!')
  const flags: boolean[] = []
  for (let r = 0; r < keys.length; r++) {
    const key = keys[r] ?? EMPTY
    if (key.kind === 'error') return key
    const below = compare('<', key, order.current)
    if (below.kind === 'error') return below
    const tied = compare('=', key, order.current)
    if (tied.kind === 'error') return tied
    flags.push((below.kind === 'number' && below.n === 1) || (tied.kind === 'number' && tied.n === 1 && r + 1 <= current))
  }
  return keepRows(shape, (r) => flags[r] === true)
}

// ── COST ──────────────────────────────────────────────────────────────────────────────────────

/** A keyword argument: omitted, empty or `""` takes the default; anything else must be one of
 *  `allowed`, in any case. The grammar has no empty arguments, hence `""` for "not given". */
function keywordArg<const K extends string>(value: FormulaValue | undefined, allowed: readonly K[], fallback: K): K | FormulaValue {
  if (value === undefined || value.kind === 'empty') return fallback
  if (value.kind === 'error') return value
  if (value.kind !== 'text') return error('#¡VALOR!')
  const word = value.s.trim().toUpperCase()
  if (word === '') return fallback
  return allowed.find((k) => k === word) ?? error('#¡VALOR!')
}

/** A quantity or cost cell as a number, `undefined` when blank, or the error to show. */
function amountOf(value: FormulaValue): number | undefined | FormulaValue {
  if (value.kind === 'empty' || (value.kind === 'text' && value.s.trim() === '')) return undefined
  const n = arithmetic([value], ([x]) => number(x))
  return n.kind === 'number' ? n.n : n
}

/** Same asset ⇔ same key: text ignores case and surrounding spaces, numbers stay numbers. */
function assetKey(value: FormulaValue): string | undefined | FormulaValue {
  switch (value.kind) {
    case 'empty':
      return undefined
    case 'error':
      return value
    case 'number':
      return `n:${value.n}`
    case 'text': {
      const s = value.s.trim()
      return s === '' ? undefined : `t:${s.toLocaleLowerCase('es')}`
    }
    case 'collection':
      return error('#¡VALOR!')
  }
}

/** Order keys compare like `<`: dates and numeric text as numbers, numbers before text. */
type OrderKey = number | string
function orderKeyOf(value: FormulaValue): OrderKey | FormulaValue {
  switch (value.kind) {
    case 'empty':
      return 0
    case 'number':
      return value.n
    case 'text': {
      const n = arithmetic([value], ([x]) => number(x))
      return n.kind === 'number' ? n.n : value.s
    }
    case 'error':
      return value
    case 'collection':
      return error('#¡VALOR!')
  }
}
const compareOrderKeys = (a: OrderKey, b: OrderKey): number =>
  typeof a === 'number' && typeof b === 'number' ? a - b
    : typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b, 'es', { sensitivity: 'base' })
      : typeof a === 'number' ? -1 : 1

const sameValue = (a: FormulaValue, b: FormulaValue): boolean => {
  switch (a.kind) {
    case 'empty':
      return b.kind === 'empty'
    case 'number':
      return b.kind === 'number' && b.n === a.n
    case 'text':
      return b.kind === 'text' && b.s === a.s
    case 'error':
      return b.kind === 'error' && b.code === a.code
    case 'collection':
      return false
  }
}
const sameColumn = (a: readonly FormulaValue[], b: readonly FormulaValue[]): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (!sameValue(a[i]!, b[i]!)) return false
  return true
}

/**
 * The grid evaluates a column formula once per row, and every row hands COST freshly built
 * copies of the same columns. Recomputing the books each time would make a column of n rows
 * cost O(n² log n); instead the last few results are kept and reused while the inputs are equal
 * value for value. Comparing is O(n) without allocating, the same order as building the
 * columns, so each row costs what the runtime already spends on it. A few entries, so several
 * COST columns evaluated side by side do not evict each other.
 */
interface CostMemo {
  readonly options: string
  readonly columns: readonly (readonly FormulaValue[])[]
  readonly result: readonly FormulaValue[]
}
const COST_MEMO_SIZE = 8
const costMemo: CostMemo[] = []

/** COST(asset; order; quantity; cost; [entry type]; [result type]; [method]). */
function costOf(
  args: readonly AstNode[],
  context: CellFormulaEvaluationContext,
  intent: FormulaResolveIntent,
  env: LetEnv,
): FormulaValue {
  const scalarArg = (i: number): FormulaValue | undefined => {
    const arg = args[i]
    return arg === undefined ? undefined : evaluateWithContext(arg, context, 'scalar', env)
  }
  const entryType = keywordArg(scalarArg(4), ['UNIT', 'TOTAL'] as const, 'TOTAL')
  if (typeof entryType !== 'string') return entryType
  const resultType = keywordArg(scalarArg(5), ['UNIT', 'TOTAL'] as const, 'TOTAL')
  if (typeof resultType !== 'string') return resultType
  const method = keywordArg(scalarArg(6), ['FIFO', 'LIFO', 'WAC'] as const, 'FIFO')
  if (typeof method !== 'string') return method

  // Columns in `costColumn`'s order: asset, quantity, cost, then order when one is given.
  const columns: (readonly FormulaValue[])[] = []
  for (const i of [0, 2, 3]) {
    const value = evaluateWithContext(args[i]!, context, 'collection', env)
    if (value.kind === 'error') return value
    columns.push(membersOf(value))
  }
  // `""` (or an empty cell) for order means row order: the grammar has no empty arguments.
  const order = evaluateWithContext(args[1]!, context, 'collection', env)
  if (order.kind === 'collection') columns.push(order.values)
  else if (order.kind === 'error') return order
  else if (!(order.kind === 'empty' || (order.kind === 'text' && order.s.trim() === ''))) return error('#¡VALOR!')
  const rows = columns[0]!.length
  if (columns.some((c) => c.length !== rows)) return error('#¡VALOR!')

  const options = `${entryType}|${resultType}|${method}`
  let hit = costMemo.findIndex((m) => m.options === options && m.columns.length === columns.length
    && m.columns.every((c, i) => sameColumn(c, columns[i]!)))
  if (hit < 0) {
    costMemo.unshift({ options, columns, result: costColumn(columns, entryType, resultType, method) })
    if (costMemo.length > COST_MEMO_SIZE) costMemo.pop()
    hit = 0
  }
  const { result } = costMemo[hit]!

  if (intent === 'collection') return collection(result)
  const current = currentRowOf(context)
  return current === undefined ? error('#¡REF!') : (result[current - 1] ?? error('#¡REF!'))
}

/** The whole COST column, aligned with the rows. A row with an error shows it and stays off the
 *  books; a row not filled in yet (no asset, no quantity, a purchase without cost) is blank and
 *  also stays off. A purchase is blank: only sales have a cost basis. */
function costColumn(
  columns: readonly (readonly FormulaValue[])[],
  entryType: 'UNIT' | 'TOTAL',
  resultType: 'UNIT' | 'TOTAL',
  method: CostMethod,
): FormulaValue[] {
  const [assets = [], quantities = [], costs = [], order] = columns
  const n = assets.length
  const out: FormulaValue[] = new Array<FormulaValue>(n).fill(EMPTY)
  const transactions: (CostTransaction | null)[] = new Array<CostTransaction | null>(n).fill(null)
  const keys: OrderKey[] = new Array<OrderKey>(n).fill(0)
  for (let r = 0; r < n; r++) {
    if (order !== undefined) {
      const key = orderKeyOf(order[r] ?? EMPTY)
      if (typeof key === 'object') {
        out[r] = key
        continue
      }
      keys[r] = key
    }
    const asset = assetKey(assets[r] ?? EMPTY)
    if (typeof asset === 'object') {
      out[r] = asset
      continue
    }
    const quantity = amountOf(quantities[r] ?? EMPTY)
    if (typeof quantity === 'object') {
      out[r] = quantity
      continue
    }
    if (asset === undefined || quantity === undefined || quantity === 0) continue
    let totalCost = 0
    if (quantity > 0) {
      const cost = amountOf(costs[r] ?? EMPTY)
      if (typeof cost === 'object') {
        out[r] = cost
        continue
      }
      if (cost === undefined) continue
      // The quantity says which way the trade went, so the cost's sign is only the sheet's
      // convention: a cash-flow column records a purchase as money out (negative).
      totalCost = Math.abs(entryType === 'UNIT' ? cost * quantity : cost)
      if (!Number.isFinite(totalCost)) {
        out[r] = error('#¡NUM!')
        continue
      }
    } else if (!Number.isFinite(quantity)) {
      out[r] = error('#¡NUM!')
      continue
    }
    transactions[r] = { asset, quantity, totalCost }
  }

  // Chronological: by order when given, row order among ties (and without order).
  const sequence = Array.from({ length: n }, (_, i) => i)
  if (order !== undefined) sequence.sort((a, b) => compareOrderKeys(keys[a]!, keys[b]!) || a - b)

  const outcomes = costBasis(transactions, sequence, method)
  for (let r = 0; r < n; r++) {
    const outcome = outcomes[r]
    const tx = transactions[r]
    if (outcome === undefined || tx === null || tx === undefined) continue
    out[r] = outcome === 'insufficient'
      ? error('#¡NUM!')
      : finite(resultType === 'UNIT' ? outcome / -tx.quantity : outcome)
  }
  return out
}

/** Evaluates the AST for ONE row with stable row/sheet/range resolution. Never throws. */
export function evaluateCellFormula(ast: AstNode, context: CellFormulaEvaluationContext): FormulaValue {
  return evaluateWithContext(ast, context, 'scalar')
}

/** Backward-compatible calculated-column evaluator. A bare `[field]` continues to resolve the
 * current row; range/cross-sheet formulas require `evaluateCellFormula`. */
export function evaluate(ast: AstNode, resolve: RefResolver): FormulaValue {
  return evaluateCellFormula(ast, {
    resolveReference: (reference) => resolve(reference.field),
    resolveRange: () => error('#¡REF!'),
  })
}
